import { drawWizardOrb, drawWizardWaveHud } from "./wizard-orbs.js";
import { enemyByKind } from "./enemy-defs.js";
import { FLIP_TILES_SIDE, flipTileTarget } from "@pd/shared";
/**
 * Canvas 2D iso viewport renderer (M1). 2:1 dimetric projection per
 * RENDERING.md: base tile 96×48, world→screen via iso.ts, player-centered
 * camera clamped to the map diamond + ~2-tile padding, continuous auto-fit
 * zoom (~20 tile-spacings across the shorter side, never below the base tile
 * contract). One painter-sorted pass over revealed drawables — floor
 * diamonds, wall low-blocks (top diamond + two side faces, ~1 tile high),
 * door billboards in the wall-strip gap, base/boss/key/stone/player
 * billboards, then the transient click-feedback markers (ground ripple /
 * interact silhouette / invalid shake) + hover outline. Unrevealed tiles are
 * void
 * (never drawn, not even as wall). Pure rendering — every position comes from
 * the latest observation (cosmetic interpolation done by the caller); the
 * client decides nothing the sim cares about.
 */
import type { PuzzleState, Observation, ObservedPlayer } from "@pd/shared";
import { FLIP_TILE_COLOURS, FlipTileAnimations } from "./flip-tiles-animation.js";
import type { Vec2 } from "./actions.js";
import { keyLetter, playerColor, playerGlow } from "./actions.js";
import {
  MARKER_CONFIG,
  clickOutlineStyle,
  groundAlpha,
  groundRx,
  invalidAlpha,
  invalidShakeX,
  markerExpired,
  markerProgress,
  outlinePath,
  outlineSprite,
} from "./markers.js";
import type { Marker, OutlineStyle } from "./markers.js";
import {
  HALF_H,
  HALF_W,
  TILE_H,
  TILE_W,
  cameraClamp,
  cameraLerp,
  painterOrder,
  snapCamera,
  tileInView,
  visibleTileRange,
} from "./iso.js";
import type { DrawKind } from "./iso.js";
import { frameUrl } from "./atlas.js";
import type { Dir, NpcDef } from "./atlas.js";
import { DIRS_CW } from "./dirs.js";
import { FLOOR_FALLBACK as C_FLOOR_FALLBACK, floorFrame as civicFloorFrame, PROP_WALL, WorldArt } from "./worldArt.js";
import { TERRAIN_FLOOR, TERRAIN_WALL } from "@pd/shared";
import { drawHero, foldDir, heroComposite, heroCompositeKey, heroFrameImg, heroVisualExtent, idleFrame, itemFrameImg, outfitForEquipment, outfitForLook, playerTint, tintForPlayer, walkFrame } from "./hero.js";
import type { HeroLut } from "./heroLut.js";
import { consumableById, ITEMS, itemByKind } from "@pd/shared";
import { spriteStore } from "./spriteStore.js";
import type { EquipSlot } from "@pd/shared";
import type { HeroAssets, PlayerAnim } from "./hero.js";
import { playerKey, enemyKey } from "./interpolate.js";
import { silhouetteForKey } from "./markers.js";
import { BLINK_FADE_MS, BLINK_MS } from "./effects.js";
import type { EffectFrame } from "./effects.js";
import { FIRE_SILHOUETTE_PASSES, ICY_SILHOUETTE_PASSES, isBound } from "./status-fx.js";

/**
 * The viewport shows ~VIEW_TILES tile-spacings across its shorter side (zoom
 * clamped so a tile is never smaller than the base 96×48 contract), so the
 * map is always larger than the view and the camera stays centered on the
 * player — clamped only at the true map edges. This is the "draw distance is
 * a camera, not information" canon: what you see is a window onto the
 * revealed map, and the Dungeon Map (M) is the overview.
 */
export const VIEW_TILES = 20;

/**
 * Cull margin, in tiles, per side: how far outside the viewport a drawable
 * may sit and still be rendered. A zero-margin cull made floor art, masonry
 * and NPC frames first appear AT the screen edge mid-walk — the visible
 * pop-in. The margin must exceed the tallest billboard's overhang (the
 * dragon's 256 px frame reaches ~5 tile-heights above its foot tile — the
 * vertical margin is 6, one tile of headroom) so nothing appears at the
 * edge, and it gives the lazy image loads (floor tiles, wall masonry and
 * NPC frames all decode on first use) lead time to decode before their
 * pixels are visible. The cost is a few extra tiles of iteration on a
 * small map — negligible.
 */
export const CULL_MARGIN_X_TILES = 3;
export const CULL_MARGIN_Y_TILES = 6;

/**
 * A resolved party look for rendering: the saved appearance packed once per
 * Welcome/RoomState (Game owns the map) — the LUT is the colour (canon
 * frames underneath) and the outfit the part variants the look picks. The
 * sim never sees it; it is presentation data over the observation.
 */
export interface PlayerLook {
  /** Null when the look recolours nothing (all-canonical colours) — the
   *  outfit below still renders, on canon frames. */
  lut: HeroLut | null;
  /** slot → variant (the account's saved outfit). */
  outfit: Record<string, string | null>;
}

/**
 * A host-contributed world prop: static art that joins the SHARED painter
 * pass, so it occludes heroes and is occluded by them exactly like a floor
 * prop. This is the seam for anything a host puts in the world that a hero
 * can walk in front of (the hub's dungeon door today, town props later) —
 * the alternative, the host overlay, draws AFTER every entity, so a big prop
 * there hides whoever is standing in front of it. The host owns the pixels;
 * the Renderer owns the depth and the camera (one painter pass, one camera —
 * AGENTS §6's one implementation).
 */
export interface WorldProp {
  /** Depth + draw anchor in tile units. Fractional is allowed and expected:
   *  a wide prop anchors on the midpoint of the tiles it stands on, which is
   *  also its centre on screen. Painter depth is x+y, so a prop standing on
   *  one anti-diagonal row (constant x+y) sorts correctly against everything
   *  on both sides of it. */
  x: number;
  y: number;
  /** Draw in MAP space at (sx0, sy0) — the anchor tile's projected top corner
   *  (the same anchor every drawable gets: the tile diamond's centre is
   *  sy0 + HALF_H·u). Called inside the renderer's camera translate, so the
   *  camera is already applied — subtracting it here too draws the prop
   *  nowhere (docs/traps/coordinate-space.md). `u` is the iso unit. */
  draw(ctx: CanvasRenderingContext2D, u: number, sx0: number, sy0: number): void;
}

/** Camera follow smoothing — time constant ≈ 1/12 s (snappy, never snaps). */
const CAM_LERP_PER_S = 12;

const C_VOID = "#0a0a0c";
const C_KEY = "#d9a441";
/** The guardian door's ward mark (design/guardian-door.md) — a warn red, distinct from the gold key letter. */
const C_GUARD = "#c23b2e";

/** `#rrggbb` + an alpha → `rgba(…)`. The outline fades with the marker's
 *  alpha (the silhouette helpers do it in outlineSprite; the diamond
 *  helpers take a finished stroke colour). */
function hexWithAlpha(hex: string, a: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${a})`;
}
export interface RenderState {
  /** Lerped positions (tile coords), keyed by entity key (p0/e0). */
  positions: Map<string, Vec2>;
  /** Per-entity animation state (dir + walk/idle cycle), keyed by entity key. */
  anims: Map<string, PlayerAnim>;
  /** The cosmetic effect frame (the class-effect visuals read its retained
   *  blink list; hosts without an effect layer may omit it). */
  effects?: EffectFrame;
  /** Tile under the cursor (hover outline). */
  hover: Vec2 | null;
  /** Transient click-feedback markers (ground ripple / interact silhouette /
   *  invalid shake) — at most one per kind, spawned on pointer-down, expired
   *  by duration. */
  markers: Marker[];
}

const DEFAULT_ANIM: PlayerAnim = { dir: "s", walkDist: 0, idleT: 0 };

/** Hero death-animation window (ms): phase 1 keels the hero over (an
 * ease-out rotation around the feet anchor), phase 2 fades it out — then
 * nothing until the sim raises the hero at the base (no bones for heroes;
 * enemy corpses keep the bones decal). */
const DEATH_ANIM_MS = 900;

/** The Squire's Sprint read: a red outline around the whole hero, fading
 *  ghost copies left on each step, and a soft red ring on the ground. The
 *  colour is the Squire's (no other class effect uses red — the damage
 *  language lives on the splats) and the whole thing dims as the buff runs
 *  out, so the fade IS the duration readout. */
const SPRINT_RED = "#ff4a3d";
/** Trail: sample the rendered position this often, keep points this long. */
const SPRINT_SAMPLE_MS = 70;
const SPRINT_TRAIL_MS = 280;
/** The last stretch of the buff over which the visuals ease down (ticks). */
const SPRINT_FADE_TICKS = 100;
/** The outline's rim width in css px (at zoom 1) and its soft-glow underlay. */
const SPRINT_RIM_PX = 2.2;

/** Per-player status and class visuals for one frame, computed from the
 *  observation + the effect frame. Absent when no effect is active. */
interface PlayerFx {
  /** Movement lock: steady icy silhouette until the authoritative deadline. */
  bound: boolean;
  /** Sprint visual strength (0 = not sprinting; ramps down near expiry). */
  sprint: number;
  /** Blink arrival fade-in alpha (1 = fully visible). */
  blinkFade: number;
  /** Blink departure ghost tile (the fade-out), when one is still alive. */
  blinkFrom?: { x: number; y: number; alpha: number };
  /** The Phoenix Teleport's arrival flame (alpha 1 → 0 across the blink's
   *  life), only when the jump carried `fx: "phoenix"`. */
  phoenix?: number;
}

type Obj =
  | { obj: "wizardOrb"; id: number; remaining: number }
  | { obj: "chasmTile"; id: number; state: number; shimmering: boolean; hint: boolean }
  | { obj: "iceTile"; pad: number; active: boolean; obstacle: boolean }
  | { obj: "puzzleTile"; id: number; colour: number; solved: boolean }
  | { obj: "terrain"; map: number; terrain: number }
  // The perimeter ring (Pass B presentation): a VIRTUAL wall tile on the
  // strip row outside the revealed room block. The sim's wall strip is never
  // revealed (obs shows void), so the room's edge reads as the strip's own
  // masonry — derived purely from the revealed block's shape + the door
  // slots (observation data). Presentation-only: nothing here is simulated.
  // The room the local hero stands in keeps NO ring — the 1.5-tall wall
  // hides the room from the inside (render's ring pass).
  | { obj: "wallring"; mask: number }
  | { obj: "door"; id: number; open: boolean; keyId: number; guarded: boolean; puzzleLocked?: boolean }
  | { obj: "base" }
  | { obj: "boss"; id: number; state: "boss" | "portal" }
  | { obj: "returnPortal" }
  | { obj: "key"; id: number }
  | { obj: "stone"; id: number; stoneKind: "personal" | "group"; ownerId: number }
  // A ground drop (design/loot.md): one item of a pile, lying flat on its
  // tile (item icon, fanned out with the other tile items). `id` is the
  // drop's entity id (the interact outline's marker key).
  | { obj: "drop"; id: number; cls: "equipment" | "consumable"; itemKind: number }
  // The base-room sack (design/loot.md) — a standing piece of furniture.
  | { obj: "sack" }
  | {
      obj: "player";
      id: number;
      name: string;
      anim: PlayerAnim;
      equipment: Record<EquipSlot, number | null>;
      look: PlayerLook | null;
      deathT?: number;
      /** The in-flight swing's charge (a charged ability swing — the client
       *  draws its glow). Absent on an uncharged swing. */
      chargeKind?: "enhanced" | "flourish" | "chain";
      /** The pending (cast-but-unspent) swing charge — the lit-ability glow
       *  before the swing starts. */
      pendingKind?: "enhanced" | "flourish" | "chain";
      /** The Barricade immunity window is holding (tick < damageImmuneUntil)
       *  — the client draws the stone shell. */
      immune?: boolean;
      reflecting?: boolean;
      /** The Flourish spin angle in radians (a 360° turn over the charged
       *  flourish swing; 0/absent = no spin). */
      spin?: number;
      /** The Squire's class-effect visuals (Sprint outline/trail/ring + the
       *  Blink fade) for this frame, or absent. (Named `classFx` — `fx` is
       *  the fractional draw coordinate on every drawable.) */
      classFx?: PlayerFx;
    }
  | { obj: "pounce-warning"; progress: number; landed: boolean; fire: boolean }
  | { obj: "pounce-target"; fire: boolean }
  | { obj: "ground-fire"; remaining: number }
  | { obj: "meteor-shadow"; progress: number }
  | { obj: "meteor-fall"; progress: number }
  | { obj: "meteor-impact"; progress: number }
  | { obj: "enemy"; id: number; npcKind: string; tint: string; anim: PlayerAnim }
  // Death-bones ground decal (arena: the sim's combat can kill a combatant).
  // `who` disambiguates player/enemy ids (both can be 0) for the fade-in map.
  | { obj: "bones"; id: number; who: "p" | "e" }
  | { obj: "marker-ground"; marker: Extract<Marker, { kind: "ground" }> }
  | { obj: "marker-invalid"; marker: Extract<Marker, { kind: "invalid" }> }
  // A host world prop (see WorldProp) — sorted with everything else.
  | { obj: "prop"; prop: WorldProp };

type Drawable = Obj & {
  /** Painter-sort coords — players sort at their ROUNDED tile (see render()). */
  x: number;
  y: number;
  /** Fractional draw coords — players draw at the lerped position. */
  fx?: number;
  fy?: number;
  kind: DrawKind;
};

export class Renderer {
  private readonly puzzleFlips = new FlipTileAnimations();

  ingestPuzzles(puzzles: readonly PuzzleState[], now: number): void {
    this.puzzleFlips.ingest(puzzles, now);
  }

  clearPuzzleAnimations(): void { this.puzzleFlips.clear(); }

  private ctx: CanvasRenderingContext2D;
  private dpr = 1;
  private cssW = 0;
  private cssH = 0;
  /** Painter-sorted drawable scratch array, reused across frames. */
  private scratch: Drawable[] = [];
  /** The current frame's cosmetic effect frame (the Sprint/Blink visuals read
   *  its retained blink list). Set at the top of render(). */
  private effects: EffectFrame | undefined;
  /** The Squire's Sprint trail: recent render positions per player id,
   *  sampled from the lerped positions (pure cosmetics). */
  private sprintTrail = new Map<number, { x: number; y: number; at: number }[]>();
  /** Camera offset in css px (set each render; read by input mapping). */
  camX = 0;
  camY = 0;
  /** Sprite atlas + caches (null → players draw nothing, floor keeps its
   *  flat-diamond fallback — the game stays playable). */
  private _heroAssets: HeroAssets | null = null;
  /** The shared world-art surface (floors, terrain props, doors, shadows) —
   *  one implementation with the lab and the rooms editor (worldArt.ts).
   *  Rebuilt whenever the atlas lands. */
  private worldArt: WorldArt | null = null;
  set heroAssets(a: HeroAssets | null) {
    this._heroAssets = a;
    this.worldArt = a ? new WorldArt(a.atlas) : null;
    this.floorImgCache.clear();
  }
  get heroAssets(): HeroAssets | null {
    return this._heroAssets;
  }
  /** The floor's tile shade (a palettes.json palette name — resolved by the
   *  host from floorId -> roomId -> floorTile; "stone" for legacy no-floor
   *  runs). The atlas entry is `floor-<name>`. */
  floorPalette = "stone";
  /** Room-level visual settings: strictly client presentation, derived from
   * the room catalog rather than simulation state. */
  architecture = "civic-stone-v1";
  wear = 24;
  /** Host world props (see WorldProp) — set by the host, joined to the
   *  painter list every frame. Never culled: there are a handful, and a
   *  wrong cull box on a prop taller than its tile hides the thing itself. */
  worldProps: WorldProp[] = [];
  /** The baked floor layer (see ensureFloorLayer). */
  private floorLayer: HTMLCanvasElement | null = null;
  /** The map-space rect (css px) the layer covers, the camera it was anchored
   *  on, and the state its pixels came from. */
  private layerX0 = 0;
  private layerY0 = 0;
  private layerW = 0;
  private layerH = 0;
  private layerCamX = 0;
  private layerCamY = 0;
  private layerU = -1;
  private layerDpr = -1;
  private layerSig = "";
  /** Camera smoothing state. */
  private camReady = false;
  private lastCamT = 0;
  /** Frame clock/scale for marker animation (set at the top of render). */
  private _now = 0;
  private _u = 1;
  private _tick = 0;
  /** Latest authoritative observation, retained only for revealed-neighbour
   * cosmetic wall joins during this render. */
  private lastObservation: Observation | null = null;
  /** Active interact markers keyed by `${obj.kind}:${obj.id}` (rebuilt each
   *  render; outlines draw inline with their entity so occlusion is free). */
  private interactMarkers = new Map<string, Marker>();
  /** NPC frame image cache (enemies) — keyed by frame path. */
  private npcLastFrames = new WeakMap<NpcDef, Map<string, HTMLImageElement>>();
  private npcImgCache = new Map<string, HTMLImageElement>();
  /** Death-bones decal (/corpses/bones.png — a generic pile, one asset for
   *  every NPC corpse; heroes get the death animation instead, no grave) —
   *  lazy-loaded when the first dead enemy appears. The asset deliberately
   *  lives OUTSIDE the game sprite tree (check-sprites forbids orphan PNGs
   *  there). */
  private bonesImg: HTMLImageElement | null = null;
  private bonesLoading = false;
  /** Birth (ms) per corpse — the enemy bones' fade-in clock AND the hero's
   *  death-animation clock (p<id>/e<id>) — stamped the first frame the dead
   *  combatant is seen, dropped when they are alive again (respawn re-arms
   *  the animation). */
  private bonesBirth = new Map<string, number>();

  constructor(private canvas: HTMLCanvasElement) {
    // `alpha: false`: every frame opens with a full-surface C_VOID fill, so the
    // buffer never needs a per-pixel blend against the page behind it — the
    // compositor gets an opaque layer and drops that work from every frame.
    this.ctx = canvas.getContext("2d", { alpha: false })!;
  }

  /** Current iso unit scale (css px per half-tile-step; ≥ 1, the 96×48 base). */
  get isoUnit(): number {
    return Math.max(1, Math.min(this.cssW, this.cssH) / (VIEW_TILES * TILE_H));
  }

  /** Viewport size in CSS px — host overlays (OverlayApi) draw in this space. */
  get viewportW(): number {
    return this.cssW;
  }
  get viewportH(): number {
    return this.cssH;
  }

  resize(): void {
    // Size from the canvas' actual layout box, not the browser window. The
    // signed-in shell reserves space for the persistent right rail and the
    // ability dock; using the window dimensions here rendered a full-screen
    // frame underneath that chrome and made pointer coordinates disagree
    // with what was visible.
    const rect = this.canvas.getBoundingClientRect();
    this.cssW = Math.max(1, rect.width);
    this.cssH = Math.max(1, rect.height);
    this.dpr = window.devicePixelRatio || 1;
    this.canvas.width = Math.floor(this.cssW * this.dpr);
    this.canvas.height = Math.floor(this.cssH * this.dpr);
    this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
  }

  render(
    obs: Observation,
    meId: number,
    st: RenderState,
    nowMs: number,
    nameFor?: (id: number) => string,
    lookFor?: (id: number) => PlayerLook | null,
    tick?: number,
  ): void {
    const { ctx } = this;
    this.lastObservation = obs;
    ctx.fillStyle = C_VOID;
    ctx.fillRect(0, 0, this.cssW, this.cssH);

    const me = st.positions.get(playerKey(meId)) ?? obs.players.find((p) => p.id === meId);
    if (!me) return;

    const u = this.isoUnit;
    this._now = nowMs;
    this._u = u;
    this._tick = tick ?? 0;
    this.effects = st.effects;
    // Camera: exponential smoothing toward the clamp of the CONTINUOUS
    // position (the lab feel) — the frame stays steady mid-step, never snaps
    // on tile arrivals or resizes. First frame snaps directly.
    const desired = cameraClamp(me, obs.width, obs.height, u, this.cssW, this.cssH);
    const lerped = cameraLerp(
      { camX: this.camX, camY: this.camY },
      desired,
      nowMs - this.lastCamT,
      CAM_LERP_PER_S,
      this.camReady,
    );
    // Device-pixel aligned (see snapCamera): the camera STATE is the snapped
    // value, so culling, hit-testing and the translate all agree and the world
    // holds still instead of resampling at a fresh sub-pixel phase every frame.
    const snapped = snapCamera(lerped, this.dpr);
    this.camX = snapped.camX;
    this.camY = snapped.camY;
    this.camReady = true;
    this.lastCamT = nowMs;

    // The ONLY cull in this file: the exact iso test (iso.ts) against a
    // PADDED viewport (CULL_MARGIN_*) — nothing starts rendering at the
    // screen edge (the pop-in), and first-use image loads start well before
    // their pixels are visible.
    const heroExtent = this.heroAssets ? heroVisualExtent(this.heroAssets.hero) : [0, 0];
    const mX = Math.max(CULL_MARGIN_X_TILES * TILE_W, heroExtent[0] ?? 0) * u;
    const mY = Math.max(CULL_MARGIN_Y_TILES * TILE_H, (heroExtent[1] ?? 0) + HALF_H) * u;
    const inView = (tx: number, ty: number): boolean =>
      tileInView(tx, ty, u, this.camX - mX, this.camY - mY, this.cssW + 2 * mX, this.cssH + 2 * mY);

    // Static floors as ONE blit (see ensureFloorLayer). Drawn in screen space
    // before the camera translate, at a device-pixel-aligned position, so the
    // layer lands 1:1 on the back buffer — no resample of baked art.
    const floorsBaked = this.ensureFloorLayer(obs, u, mX, mY);
    if (floorsBaked && this.floorLayer) {
      const q = this.dpr;
      ctx.drawImage(
        this.floorLayer,
        Math.round((this.layerX0 - this.camX) * q) / q,
        Math.round((this.layerY0 - this.camY) * q) / q,
        this.layerW,
        this.layerH,
      );
    }

    ctx.save();
    ctx.translate(-this.camX, -this.camY);

    const list = this.scratch;
    list.length = 0;

    // Terrain: revealed tiles, visible range only. Every revealed tile gets
    // its floor (the room's tile shade), and unwalkable tiles add their
    // terrain prop (wall/pond/table from obs.terrain — the room recipe's
    // furniture; 0 = bare wall-strip floor).
    const range = visibleTileRange(obs.width, obs.height, u, this.camX - mX, this.camY - mY, this.cssW + 2 * mX, this.cssH + 2 * mY);
    if (range) {
      for (let ty = range.y0; ty <= range.y1; ty++) {
        for (let tx = range.x0; tx <= range.x1; tx++) {
          const i = ty * obs.width + tx;
          if (obs.revealed[i] !== 1) continue; // void — never drawn
          if (!inView(tx, ty)) continue;
          const tileMap = obs.map[i] ?? 0;
          const tileTerrain = obs.terrain[i] ?? TERRAIN_FLOOR;
          // Bare floor is already on the baked layer — ~1,000 of the painter
          // entries on a revealed map, and none of them needs to depth-sort.
          // Obstacle tiles stay: their art stands UP off the tile and must
          // interleave with the entities behind and in front of it.
          if (floorsBaked && (tileMap === 1 || tileTerrain === TERRAIN_FLOOR)) continue;
          list.push({
            kind: "terrain",
            obj: "terrain",
            map: tileMap,
            terrain: tileTerrain,
            x: tx,
            y: ty,
          });
        }
      }

      // The perimeter ring (Pass B, civic-stone-v1 only): the revealed room
      // block's edge reads as the wall strip's masonry. The sim's strip is
      // never revealed (the observation zero-fills unrevealed map/terrain),
      // so these tiles are PRESENTATION — derived from the revealed block's
      // shape alone + the door slots (which are in the observation). A door
      // slot's far side is the next room's floor: void until revealed, never
      // a ring tile — no unrevealed room geometry is inferred. The margin is
      // one tile past the visible range (the ring hugs the block's edge, and
      // it runs outside the block).
      if (this.architecture === "civic-stone-v1" && this.worldArt) {
        const doorSlots = new Set<number>(obs.doors.map((d) => d.x + d.y * obs.width));
        // The room the local hero stands in keeps NO ring: the 1.5-tall
        // masonry hides too much of the room from the inside — the room you
        // are in has no walls on screen. Presentation only: the sim's wall
        // strip still blocks movement and spawns (AGENTS §6), and the room's
        // doors keep their stone frames (separate entities). The test covers
        // the T×T room block plus its 1-tile outline — exactly where the
        // ring lives (the side runs + the convex corner tiles). Same room
        // cell decomposition as the sim's roomOf/revealPlayer (stride =
        // roomTiles + 1). No me in the obs (the leaver's last tick) → no box.
        const meP = obs.players.find((p) => p.id === meId);
        const T = obs.combat.roomTiles;
        const stride = T + 1;
        const bx0 = meP ? Math.floor((meP.x + 1) / stride) * stride - 1 : Infinity;
        const by0 = meP ? Math.floor((meP.y + 1) / stride) * stride - 1 : Infinity;
        const bx1 = meP ? bx0 + T + 1 : -Infinity;
        const by1 = meP ? by0 + T + 1 : -Infinity;
        for (let ty = range.y0 - 1; ty <= range.y1 + 1; ty++) {
          for (let tx = range.x0 - 1; tx <= range.x1 + 1; tx++) {
            // The ring is UNREVEAL-SPACE only: a revealed tile is the floor
            // (the terrain pass draws its tile art + any in-room wall) — a
            // wall block there would cover the floor's tile shade and the
            // floor type's colour is user content (the Floors store). Only
            // the void tiles outside the block (the sim's never-revealed
            // strip row) carry the presentation masonry.
            if (tx >= 0 && ty >= 0 && tx < obs.width && ty < obs.height && obs.revealed[ty * obs.width + tx] === 1) continue;
            if (!this.isRingWall(tx, ty, doorSlots)) continue;
            // No walls on the room I am standing in (see above).
            if (tx >= bx0 && tx <= bx1 && ty >= by0 && ty <= by1) continue;
            if (!inView(tx, ty)) continue;
            list.push({ kind: "terrain", obj: "wallring", x: tx, y: ty, mask: this.ringMask(tx, ty, doorSlots) });
          }
        }
      }
    }

    // Host world props join the pass here (depth-sorted with the entities).
    for (const p of this.worldProps) {
      list.push({ kind: "entity", obj: "prop", x: p.x, y: p.y, prop: p });
    }

    // Entities — the server already filtered these to revealed tiles; the
    // client only skips the ones outside the viewport.
    for (const enemy of obs.enemies) {
      const wave = enemy.alive ? enemy.orbWave : undefined;
      if (!wave || wave.startsAt < 0) continue;
      for (const orb of wave.orbs) if (inView(orb.x, orb.y)) list.push({ kind: "entity", obj: "wizardOrb", ...orb,
        remaining: Math.max(0, (wave.expiresAt - this._tick) / (wave.expiresAt - wave.startsAt)) });
    }
    for (const puzzle of obs.puzzles ?? []) {
      if (puzzle.kind === "orb-wizard") continue;
      if (puzzle.kind === "chasm-crossing") {
        for (let i = 0; i < puzzle.side * puzzle.side; i++) {
          const x = puzzle.x + i % puzzle.side, y = puzzle.y + Math.floor(i / puzzle.side);
          if (puzzle.normalTiles.includes(i) || !inView(x, y) || obs.revealed[y * obs.width + x] !== 1) continue;
          const s = puzzle.shimmer;
          const shimmering = !!s && (s.axis === "row" ? Math.floor(i / puzzle.side) : i % puzzle.side) === s.line;
          list.push({ kind: "terrain", obj: "chasmTile", id: y * obs.width + x, x, y, state: puzzle.tiles[i]!, shimmering, hint: shimmering && !!s?.safeTiles.includes(i) });
        }
        continue;
      }
      if (puzzle.kind === "icy-pressure-pads") {
        for (let i = 0; i < puzzle.side * puzzle.side; i++) {
          const x = puzzle.x + i % puzzle.side, y = puzzle.y + Math.floor(i / puzzle.side);
          if (puzzle.safeTiles.includes(i) || !inView(x, y) || obs.revealed[y * obs.width + x] !== 1) continue;
          const pad = puzzle.pads.findIndex((pad) => pad.index === i);
          list.push({ kind: "terrain", obj: "iceTile", x, y, pad, active: pad >= 0 && puzzle.pads[pad]!.active, obstacle: puzzle.obstacles.includes(i) });
        }
        continue;
      }
      for (let i = 0; i < puzzle.colours.length; i++) {
        const x = puzzle.x + i % FLIP_TILES_SIDE;
        const y = puzzle.y + Math.floor(i / FLIP_TILES_SIDE);
        if (!inView(x, y) || obs.revealed[y * obs.width + x] !== 1) continue;
        list.push({ kind: "terrain", obj: "puzzleTile", id: flipTileTarget(puzzle.id, i), x, y, colour: puzzle.colours[i]!, solved: puzzle.solved });
      }
    }
    for (const d of obs.doors) {
      if (!inView(d.x, d.y)) continue;
      list.push({ kind: "entity", obj: "door", x: d.x, y: d.y, id: d.id, open: d.open, keyId: d.keyId, guarded: d.guarded, puzzleLocked: d.puzzleLocked });
    }
    if (inView(obs.base.x, obs.base.y)) {
      list.push({ kind: "entity", obj: "base", x: obs.base.x, y: obs.base.y });
    }
    if (obs.boss && inView(obs.boss.x, obs.boss.y)) {
      list.push({ kind: "entity", obj: "boss", id: obs.boss.id, x: obs.boss.x, y: obs.boss.y, state: obs.boss.state });
    }
    // The return portal (run.md): the base room's way back to the hub.
    // Reveal-gated by the server (obs.returnPortal is null until the base
    // room is open — which is at spawn, so it is present from tick 0).
    if (obs.returnPortal && inView(obs.returnPortal.x, obs.returnPortal.y)) {
      list.push({ kind: "entity", obj: "returnPortal", x: obs.returnPortal.x, y: obs.returnPortal.y });
    }
    // Floor items (keys + stones + drops) bucket by tile. A pile is a compact
    // grounded heap: offsets run along screen-horizontal (x - y), not along
    // one tile axis, so the stack never stairs down the isometric plane.
    const STACK_GAP = 0.12; // screen-horizontal tile units between layers
    const itemStacks = new Map<string, Drawable[]>();
    const stackItems = (d: Drawable): void => {
      const k = `${d.x},${d.y}`;
      const arr = itemStacks.get(k);
      if (arr) arr.push(d);
      else itemStacks.set(k, [d]);
    };
    for (const k of obs.keys) {
      if (!inView(k.x, k.y)) continue;
      stackItems({ kind: "entity", obj: "key", x: k.x, y: k.y, id: k.id });
    }
    for (const s of obs.stones) {
      if (s.state !== "floor") continue;
      const sx = s.x ?? 0;
      const sy = s.y ?? 0;
      if (!inView(sx, sy)) continue;
      stackItems({ kind: "entity", obj: "stone", id: s.id, x: sx, y: sy, stoneKind: s.kind, ownerId: s.ownerId });
    }
    // The sack stands in the base room: it joins the pass BEFORE the
    // fan-out so a pile on its tile (a death on the sack tile) draws in
    // front of it (painter order = insertion order at equal depth).
    const sack = obs.sack;
    if (sack && inView(sack.x, sack.y)) {
      list.push({ kind: "entity", obj: "sack", x: sack.x, y: sack.y });
    }
    // Ground drops join the same tile fan-out (a pile = several items on one
    // tile). obs.drops is id-ascending (spawn order), so the TOP (highest
    // id) is pushed last and draws front-most within the pile.
    for (const d of obs.drops) {
      if (!inView(d.x, d.y)) continue;
      stackItems({ kind: "entity", obj: "drop", id: d.id, x: d.x, y: d.y, cls: d.cls, itemKind: d.kind });
    }
    // A ground item on a tile a hero stands on would draw UNDER the hero
    // (items are pushed before players — painter order) and vanish: a
    // "summoned" gatestone was sitting invisible at the caster's own feet.
    // Step the stack out beside the hero instead: a compact fan for a stack,
    // a fixed screen-horizontal offset for a lone item. Presentation only —
    // the item's real tile is unchanged (sort tile + click tile), the sim
    // never sees it.
    const occupiedTile = new Set<number>();
    for (const p of obs.players) if (p.alive) occupiedTile.add(p.x + p.y * obs.width);
    for (const items of itemStacks.values()) {
      const n = items.length;
      const head = items[0]!;
      const onHero = occupiedTile.has(head.x + head.y * obs.width);
      const gap = onHero ? 0.62 : STACK_GAP;
      for (let i = 0; i < n; i++) {
        const it = items[i]!;
        if (n > 1) {
          const screenOffset = (i - (n - 1) / 2) * gap;
          it.fx = it.x + screenOffset / 2;
          it.fy = it.y - screenOffset / 2;
        }
        // A lone item goes LEFT (−x): the hero's held gatestone (a carried
        // stone composites in the hand, screen-right) would cover an item
        // offset to the right.
        else if (onHero) {
          const screenOffset = -0.55;
          it.fx = it.x + screenOffset / 2;
          it.fy = it.y - screenOffset / 2;
        }
        list.push(it);
      }
    }
    for (const p of obs.players) {
      if (!p.alive) {
        // The hero gets a DEATH ANIMATION, not a grave: keel over + fade
        // out (drawPlayerSprite's deathT), then nothing until the sim raises
        // the hero at the base. The stamp is the death clock (dropped when
        // the hero is alive again — respawn re-arms the animation).
        const dkey = `p${p.id}`;
        if (!this.bonesBirth.has(dkey)) this.bonesBirth.set(dkey, nowMs);
        const deathT = (nowMs - (this.bonesBirth.get(dkey) ?? nowMs)) / DEATH_ANIM_MS;
        if (deathT < 1 && inView(p.x, p.y)) {
          list.push({
            kind: "entity",
            obj: "player",
            x: p.x,
            y: p.y,
            fx: p.x,
            fy: p.y,
            id: p.id,
            name: nameFor?.(p.id) ?? p.name,
            anim: st.anims.get(playerKey(p.id)) ?? DEFAULT_ANIM,
            equipment: p.equipment,
            look: lookFor?.(p.id) ?? null,
            deathT,
          });
        }
        continue;
      }
      const pos = st.positions.get(playerKey(p.id)) ?? { x: p.x, y: p.y };
      if (!inView(pos.x, pos.y)) continue;
      // The Flourish turn: a full 360° whirl, and it's FASTER than the swing —
      // the hero completes the turn over the first FLORISH_SPIN_FRACTION of the
      // swing (a snappy spin), then holds the final facing while the weapon
      // finishes its arc. The old full-swing turn dragged. The hit still lands
      // at the swing's impactTick — the spin is a pure visual read, it never
      // moves the sim. Progress is tick-driven (continuous), so the turn is
      // smooth even though the attack ANIM under it is 6 discrete frames.
      const FLORISH_SPIN_FRACTION = 0.6; // tune: 1 = full swing, lower = snappier
      let spin = 0;
      if (tick !== undefined && p.attack?.chargeKind === "flourish") {
        const span = Math.max(1, p.attack.impactTick - p.attack.startTick);
        const progress = Math.max(0, Math.min(1, (tick - p.attack.startTick) / span));
        spin = Math.min(1, progress / FLORISH_SPIN_FRACTION) * Math.PI * 2;
      }
      // Sort at the ROUNDED tile (the boots-vs-front-tile clip fix: during the
      // second half of a step round(pos) == the target tile, so the tile in
      // front draws BEFORE the hero; walls keep correct occlusion at every
      // depth). Draw at the fractional position (fx/fy).
      list.push({
        kind: "entity",
        obj: "player",
        x: Math.round(pos.x),
        y: Math.round(pos.y),
        fx: pos.x,
        fy: pos.y,
        id: p.id,
        name: nameFor?.(p.id) ?? p.name,
        anim: p.iceSlide ? { ...(st.anims.get(playerKey(p.id)) ?? DEFAULT_ANIM), walkDist: 0 } : st.anims.get(playerKey(p.id)) ?? DEFAULT_ANIM,
        equipment: p.equipment,
        look: lookFor?.(p.id) ?? null,
        // Class ability state (classes.md) — the client's ability reads.
        chargeKind: p.attack?.chargeKind ?? undefined,
        pendingKind: p.pendingHit?.kind ?? undefined,
        immune: tick !== undefined && p.damageImmuneUntil > tick,
        reflecting: tick !== undefined && p.reflectUntil > tick,
        spin,
        // The Squire's Sprint/Blink visuals (computed from the observation +
        // the retained blink list; undefined for every other frame).
        classFx: this.playerFx(p, pos, nowMs, tick),
      });
    }
    // Enemies (the arena; a dungeon floor has none) — same painter pass + the
    // same anim model as the hero (attack frame when swinging, else walk/idle).
    for (const e of obs.enemies) {
      if (!e.alive) {
        // The corpse's bones: ground decal on the death tile (sorted as
        // terrain ONE ROW UP so it renders under any standing combatant).
        // Gated by the death table's drops_bones (design/loot.md): ghosts
        // leave no bones (the flag is dead-only, default true — an NPC with
        // no table keeps the pre-loot decal).
        if (e.bones !== false && inView(e.x, e.y)) {
          list.push({ kind: "terrain", obj: "bones", x: e.x, y: e.y - 1, fx: e.x, fy: e.y, id: e.id, who: "e" });
        }
        continue;
      }
      const leap=e.pounce;
      const shower = e.meteorShower;
      if (shower && tick !== undefined && tick >= shower.startTick && tick < shower.endTick) {
        const landed = tick >= shower.impactTick;
        const progress = landed ? (tick - shower.impactTick) / Math.max(1, shower.endTick - shower.impactTick)
          : (tick - shower.startTick) / Math.max(1, shower.impactTick - shower.startTick);
        for (const tile of shower.tiles) {
          if (!inView(tile.x, tile.y)) continue;
          list.push({ kind: "terrain", obj: landed ? "meteor-impact" : "meteor-shadow", x: tile.x, y: tile.y, progress });
          if (!landed && progress >= 0.65) list.push({ kind: "entity", obj: "meteor-fall", x: tile.x, y: tile.y, progress: (progress - 0.65) / 0.35 });
        }
      }
      for (const fire of e.groundFire ?? []) {
        if (tick === undefined || tick < fire.startTick || tick >= fire.endTick) continue;
        for (const tile of fire.tiles) {
          if (inView(tile.x,tile.y)) list.push({kind:"terrain",obj:"ground-fire",x:tile.x,y:tile.y,remaining:(fire.endTick-tick)/(fire.endTick-fire.startTick)});
        }
      }
      if (leap && tick !== undefined && tick < leap.endTick) {
        const landed=tick>=leap.landTick;
        const progress=landed ? (tick-leap.landTick)/(leap.endTick-leap.landTick) : (tick-leap.startTick)/(leap.landTick-leap.startTick);
        for (const tile of leap.tiles) {
          if (inView(tile.x,tile.y)) list.push({kind:"terrain",obj:"pounce-warning",x:tile.x,y:tile.y,progress,landed,fire:leap.element==="fire"});
        }
        const target=obs.players.find((p)=>p.id===leap.targetId&&p.alive);
        if(target&&!landed&&inView(target.x,target.y)) {
          const pos=st.positions.get(playerKey(target.id))??target;
          list.push({kind:"entity",obj:"pounce-target",x:Math.round(pos.x),y:Math.round(pos.y),fx:pos.x,fy:pos.y,fire:leap.element==="fire"});
        }
      }
      const pos = st.positions.get(enemyKey(e.id)) ?? { x: e.x, y: e.y };
      if (!inView(pos.x, pos.y)) continue;
      list.push({
        kind: "entity",
        obj: "enemy",
        x: Math.round(pos.x),
        y: Math.round(pos.y),
        fx: pos.x,
        fy: pos.y,
        id: e.id,
        npcKind: e.kind,
        tint: e.tint,
        anim: st.anims.get(enemyKey(e.id)) ?? DEFAULT_ANIM,
      });
    }

    // Transient click markers: ground + invalid join the painter pass at
    // their tile (an in-front wall occludes them like any drawable);
    // interact markers are keyed for the inline outline in drawDrawable.
    this.interactMarkers.clear();
    for (const m of st.markers) {
      if (markerExpired(m, nowMs, MARKER_CONFIG)) continue;
      if (m.kind === "interact") {
        // The return portal has no id (one per floor) — "self" as its key.
        const id = "id" in m.obj ? m.obj.id : "self";
        this.interactMarkers.set(`${m.obj.kind}:${id}`, m);
      } else if (inView(m.x, m.y)) {
        if (m.kind === "ground") {
          list.push({ kind: "entity", obj: "marker-ground", x: m.x, y: m.y, marker: m });
        } else {
          list.push({ kind: "entity", obj: "marker-invalid", x: m.x, y: m.y, marker: m });
        }
      }
    }

    // Death bones: lazy-load the decal, stamp each pile's birth for the
    // fade-in, and drop the clocks of combatants alive again (respawn;
    // heroes reuse the same map as their death-animation clock).
    for (const d of list) {
      if (d.obj !== "bones") continue;
      const key = `${d.who}${d.id}`;
      if (!this.bonesBirth.has(key)) this.bonesBirth.set(key, nowMs);
      if (!this.bonesImg && !this.bonesLoading) {
        this.bonesLoading = true;
        const packed = spriteStore();
        if (packed?.has("corpses/bones.png")) {
          this.bonesImg = packed.image("corpses/bones.png");
          continue;
        }
        const im = new Image();
        im.onload = () => {
          this.bonesImg = im;
        };
        im.onerror = () => {
          this.bonesLoading = false;
        };
        im.src = "/corpses/bones.png";
      }
    }
    for (const p of obs.players) if (p.alive) this.bonesBirth.delete(`p${p.id}`);
    for (const e of obs.enemies) if (e.alive) this.bonesBirth.delete(`e${e.id}`);

    list.sort(painterOrder);

    for (const d of list) this.drawDrawable(d, u, meId);

    // Hover outline: drawn after the pass at the projected tile position
    // (hover is unchanged — tile diamond + readout text, no marker preview).
    if (st.hover) this.drawHover(st.hover, u);

    ctx.restore();
    drawWizardWaveHud(ctx, obs, meId, this._tick, this.cssW);
  }

  // ---- drawables ---------------------------------------------------------

  private drawDrawable(d: Drawable, u: number, meId: number): void {
    // Map-space anchor: the ctx.translate(-camX, -camY) in render() applies
    // the camera exactly once — subtracting it here too would double-shift
    // every drawable off-screen. Players draw at their fractional position
    // (fx/fy); everything else is integer-tiled.
    const dx = d.fx ?? d.x;
    const dy = d.fy ?? d.y;
    const sx0 = (dx - dy) * HALF_W * u;
    const sy0 = (dx + dy) * HALF_H * u;
    switch (d.obj) {
      case "iceTile": {
        const ctx = this.ctx;
        this.diamond(sx0, sy0, u, d.obstacle ? "#465c70" : "#82b7d0");
        this.diamondOutline(sx0, sy0, u, "#b9e4ef", 0.7);
        if (d.obstacle) {
          // Side faces make the fixed stopper read as a raised ice block.
          ctx.fillStyle = "#658da5";
          ctx.beginPath(); ctx.moveTo(sx0 - 38 * u, sy0 + 7 * u); ctx.lineTo(sx0, sy0 + 26 * u); ctx.lineTo(sx0, sy0 + 38 * u); ctx.lineTo(sx0 - 38 * u, sy0 + 19 * u); ctx.closePath(); ctx.fill();
          ctx.fillStyle = "#82afc6";
          ctx.beginPath(); ctx.moveTo(sx0 + 38 * u, sy0 + 7 * u); ctx.lineTo(sx0, sy0 + 26 * u); ctx.lineTo(sx0, sy0 + 38 * u); ctx.lineTo(sx0 + 38 * u, sy0 + 19 * u); ctx.closePath(); ctx.fill();
          this.diamond(sx0, sy0 - 12 * u, u * 0.8, "#c7dce6");
          this.diamondOutline(sx0, sy0 - 12 * u, u * 0.8, "#5e7f99", 2);
        } else if (d.pad >= 0) {
          this.diamond(sx0, sy0 + 4 * u, u * 0.8, d.active ? "#287c62" : "#29485f");
          this.diamondOutline(sx0, sy0 + 4 * u, u * 0.8, d.active ? "#92f2be" : "#f0d694", 2);
          this.label(sx0, sy0 + HALF_H * u, u, d.active ? "✓" : String(d.pad + 1), d.active ? "#c5ffdd" : "#fff0be", 0.3);
        } else {
          ctx.strokeStyle = "#b4dce9"; ctx.lineWidth = u;
          ctx.beginPath(); ctx.moveTo(sx0 - 12 * u, sy0 + 10 * u); ctx.lineTo(sx0 + 8 * u, sy0 + 17 * u); ctx.stroke();
        }
        break;
      }
      case "chasmTile": {
        // Flat procedural geometry: three readable states over the same tile footprint.
        this.diamond(sx0, sy0, u, d.state === 1 ? "#080b13" : d.state === 2 ? "#65766d" : "#414657");
        this.diamondOutline(sx0, sy0, u * 0.94, d.state === 1 ? "#262c40" : d.state === 2 ? "#a4b8a7" : "#62697c", 1);
        if (d.state === 1) {
          this.diamond(sx0, sy0 + 5 * u, u * 0.68, "#03050a");
        } else if (d.state === 2) {
          this.label(sx0, sy0 + HALF_H * u, u, "·", "#d9e8d7", 0.35);
        }
        if (d.shimmering) this.diamondOutline(sx0, sy0, u * 0.83, d.hint ? "#c9e3d1" : "#7d8ca8", d.hint ? 2 : 1);
        if (d.hint) {
          const ctx = this.ctx;
          ctx.strokeStyle = "#e0efdc"; ctx.lineWidth = 1.5 * u;
          ctx.beginPath(); ctx.moveTo(sx0 - 6 * u, sy0 + HALF_H * u); ctx.lineTo(sx0 + 6 * u, sy0 + HALF_H * u);
          ctx.moveTo(sx0, sy0 + HALF_H * u - 4 * u); ctx.lineTo(sx0, sy0 + HALF_H * u + 4 * u); ctx.stroke();
        }
        if (this.interactMarkers.has(`chasmTile:${d.id}`)) this.diamondOutline(sx0, sy0, u, "#75dfff", 3);
        break;
      }
      case "wizardOrb":
        drawWizardOrb(this.ctx, sx0, sy0, u, d.remaining);
        break;
      case "puzzleTile": {
        const ctx = this.ctx;
        const visual = this.puzzleFlips.sample(d.id, this._now) ?? { colour: d.colour, scale: 1, lift: 0 };
        const cy = sy0 + HALF_H * u;
        // The socket remains on the floor. Only its face lifts and turns;
        // items/heroes keep their normal painter order and hit geometry.
        this.diamond(sx0, sy0, u, "#15151d");
        ctx.save();
        ctx.translate(sx0, cy - visual.lift * u);
        ctx.scale(1, visual.scale);
        this.diamond(0, -HALF_H * u, u, "#333444");
        this.diamond(0, -HALF_H * u + 3 * u, u * 0.87, FLIP_TILE_COLOURS[visual.colour === 0 ? 0 : 1]);
        this.diamondOutline(0, -HALF_H * u, u, d.solved ? "#9fd7a8" : "#b8b0ce", 1);
        // Distinct symbols keep the two states legible without relying on colour alone.
        ctx.strokeStyle = "#eee8f5";
        ctx.lineWidth = 1.8 * u;
        ctx.beginPath();
        if (visual.colour === 0) ctx.ellipse(0, 0, 8 * u, 4 * u, 0, 0, Math.PI * 2);
        else { ctx.moveTo(-8 * u, 0); ctx.lineTo(8 * u, 0); ctx.moveTo(0, -4 * u); ctx.lineTo(0, 4 * u); }
        ctx.stroke();
        ctx.restore();
        if (this.interactMarkers.has(`puzzleTile:${d.id}`)) this.diamondOutline(sx0, sy0, u, "#75dfff", 3);
        break;
      }
      case "terrain":
        this.drawTerrainTile(sx0, sy0, u, d.x, d.y, d.map, d.terrain);
        break;
      case "wallring":
        this.drawWallRing(sx0, sy0, u, d.mask);
        break;
      case "bones":
        this.drawBonesDecal(sx0, sy0, u, d);
        break;
      case "prop":
        // Map space, camera already translated by render() — the prop gets
        // its anchor and draws from there.
        d.prop.draw(this.ctx, u, sx0, sy0);
        break;
      case "door":
        this.drawDoorOutline(sx0, sy0, u, d.open, this.interactMarkers.get(`door:${d.id}`));
        this.drawDoor(sx0, sy0, u, d.open, d.keyId, d.guarded, d.puzzleLocked);
        break;
      case "base":
        // No marker: the base is the spawn tile and plain floor (2026-09-26:
        // the green diamond was removed — it read as a "weird square" in the
        // middle of the room). The hero spawns here; respawns announce in chat.
        break;
      case "boss":
        this.drawBossOutline(sx0, sy0, u, this.interactMarkers.get(`boss:${d.id}`));
        if (d.state === "portal") {
          this.drawPortal(sx0, sy0, u, this._tick);
        }
        // state "boss": nothing on the tile (the red diamond was removed,
        // 2026-09-26) — the boss MONSTER is the visible thing in its room;
        // the portal appears the tick the boss dies. Interact (Enter) is
        // rejected while it lives ("the boss is still alive").
        break;
      case "returnPortal":
        this.drawReturnPortal(sx0, sy0, u, this._tick);
        break;
      case "key":
        this.drawKeyOutline(sx0, sy0, u, d.id, this.interactMarkers.get(`key:${d.id}`));
        this.drawKey(sx0, sy0, u, d.id);
        break;
      case "sack":
        this.drawSackOutline(sx0, sy0, u, this.interactMarkers.get("sack:self"));
        this.drawSack(sx0, sy0, u);
        break;
      case "drop":
        this.drawDropOutline(sx0, sy0, u, d, this.interactMarkers.get(`item:${d.id}`));
        this.drawDrop(sx0, sy0, u, d);
        break;
      case "stone":
        // Glow under the outline (so the cyan ring reads clearly), then the
        // sprite on top (the outline ring stays visible around it).
        this.drawStoneGlow(sx0, sy0, u, d.stoneKind, d.ownerId);
        this.drawStoneOutline(sx0, sy0, u, d.stoneKind, d.ownerId, this.interactMarkers.get(`stone:${d.id}`));
        this.drawStoneSprite(sx0, sy0, u, d.stoneKind, d.ownerId);
        break;
      case "player":
        this.drawPlayerSprite(
          sx0,
          sy0,
          u,
          d.id,
          d.name,
          meId,
          d.anim,
          d.equipment,
          d.look,
          d.deathT,
          d.spin,
          d.pendingKind ?? d.chargeKind,
          d.immune,
          d.reflecting,
          d.classFx,
        );
        break;
      case "enemy":
        // Silhouette flash on an attack (the interact marker) — drawn BEFORE
        // the sprite so the sprite covers the inner half of the ring (the
        // key/stone outline pattern).
        this.drawEnemyOutline(sx0, sy0, u, d.npcKind, d.tint, d.anim, this.interactMarkers.get(`enemy:${d.id}`));
        this.drawEnemySprite(sx0, sy0, u, d.npcKind, d.tint, d.anim);
        break;
      case "pounce-warning": {
        const ctx=this.ctx;
        ctx.save();
        ctx.globalAlpha=d.landed ? Math.max(0,1-d.progress)*0.7 : 0.3+d.progress*0.35;
        this.diamond(sx0,sy0,u,d.fire ? d.landed ? "#ffe59d" : "#ff722e" : d.landed?"#e5fbff":"#75dfff");
        ctx.strokeStyle=d.fire?"#ffe59d":"#e5fbff";ctx.lineWidth=1.5*u;
        ctx.stroke();
        ctx.restore();
        break;
      }
      case "pounce-target": {
        const ctx=this.ctx;const cy=sy0+HALF_H*u;
        ctx.save();ctx.strokeStyle=d.fire?"#ffe59d":"#e5fbff";ctx.fillStyle=d.fire?"#ff722e":"#75dfff";ctx.lineWidth=2*u;
        ctx.beginPath();ctx.ellipse(sx0,cy,18*u,8*u,0,0,Math.PI*2);ctx.stroke();
        ctx.beginPath();ctx.moveTo(sx0,cy-112*u);ctx.lineTo(sx0-6*u,cy-123*u);ctx.lineTo(sx0+6*u,cy-123*u);ctx.closePath();ctx.fill();ctx.restore();
        break;
      }
      case "meteor-shadow": {
        const ctx = this.ctx;
        const cy = sy0 + HALF_H * u;
        ctx.save();
        ctx.globalAlpha = 0.25 + d.progress * 0.3;
        this.diamond(sx0, sy0, u, "#20151e");
        ctx.globalAlpha = 0.7;
        ctx.fillStyle = "#100c13";
        ctx.beginPath(); ctx.ellipse(sx0, cy, (15 + 23 * d.progress) * u, (7 + 12 * d.progress) * u, 0, 0, Math.PI * 2); ctx.fill();
        // The rim outlines the exact single-tile footprint throughout the tell.
        ctx.globalAlpha = 0.75;
        this.diamond(sx0, sy0, u, "rgba(0,0,0,0)");
        ctx.strokeStyle = d.progress > 0.65 ? "#ff8b38" : "#a47768"; ctx.lineWidth = 1.5 * u; ctx.stroke();
        ctx.restore(); break;
      }
      case "meteor-fall": {
        const ctx = this.ctx;
        const cy = sy0 + HALF_H * u;
        const height = (1 - d.progress) * 190 * u;
        const x = sx0 - height * 0.22, y = cy - height;
        ctx.save();
        ctx.fillStyle = "#ff722e";
        ctx.beginPath(); ctx.moveTo(x - 11 * u, y); ctx.lineTo(x - 22 * u, y - 58 * u); ctx.lineTo(x + 13 * u, y - 12 * u); ctx.closePath(); ctx.fill();
        ctx.fillStyle = "#ffe59d";
        ctx.beginPath(); ctx.moveTo(x - 4 * u, y); ctx.lineTo(x - 14 * u, y - 38 * u); ctx.lineTo(x + 6 * u, y - 5 * u); ctx.closePath(); ctx.fill();
        ctx.fillStyle = "#48242b"; ctx.strokeStyle = "#ff9b43"; ctx.lineWidth = 2 * u;
        ctx.beginPath(); ctx.moveTo(x - 11 * u, y - 10 * u); ctx.lineTo(x + 5 * u, y - 14 * u);
        ctx.lineTo(x + 14 * u, y - 2 * u); ctx.lineTo(x + 7 * u, y + 9 * u); ctx.lineTo(x - 9 * u, y + 8 * u); ctx.closePath(); ctx.fill(); ctx.stroke();
        ctx.restore(); break;
      }
      case "meteor-impact": {
        const ctx = this.ctx;
        const cy = sy0 + HALF_H * u;
        ctx.save(); ctx.globalAlpha = Math.max(0, 1 - d.progress);
        this.diamond(sx0, sy0, u, d.progress < 0.25 ? "#ffe59d" : "#8f291b");
        ctx.strokeStyle = "#ff722e"; ctx.lineWidth = (4 - 3 * d.progress) * u;
        ctx.beginPath(); ctx.ellipse(sx0, cy, (12 + 31 * d.progress) * u, (6 + 15 * d.progress) * u, 0, 0, Math.PI * 2); ctx.stroke();
        for (let i = 0; i < 5; i++) {
          const angle = i * Math.PI * 2 / 5;
          const radius = (10 + 24 * d.progress) * u;
          ctx.fillStyle = i % 2 ? "#ff722e" : "#ffe59d";
          ctx.fillRect(sx0 + Math.cos(angle) * radius, cy + Math.sin(angle) * radius * 0.5 - Math.sin(d.progress * Math.PI) * 22 * u, 3 * u, 3 * u);
        }
        ctx.restore(); break;
      }
      case "ground-fire": {
        const ctx=this.ctx;
        ctx.save();ctx.globalAlpha=Math.min(1,d.remaining*6)*0.85;
        this.diamond(sx0,sy0,u,"#8f291b");
        ctx.strokeStyle="#ff8b38";ctx.lineWidth=1.5*u;ctx.stroke();
        // Small tongues rooted in the server's tiles. Flicker is cosmetic;
        // neither coverage nor damage is calculated by the renderer.
        for (let i=0;i<3;i++) {
          const x=sx0+(i-1)*17*u, y=sy0+(HALF_H+(i===1?5:-2))*u;
          const height=(13+4*Math.sin(this._now/130+d.x*3+d.y*5+i*2))*u;
          ctx.fillStyle="#ff722e";ctx.beginPath();
          ctx.moveTo(x-5*u,y);ctx.quadraticCurveTo(x-7*u,y-height*0.5,x+2*u,y-height);
          ctx.quadraticCurveTo(x+8*u,y-height*0.3,x+5*u,y);ctx.closePath();ctx.fill();
          ctx.fillStyle="#ffe59d";ctx.beginPath();
          ctx.moveTo(x-2*u,y);ctx.lineTo(x,y-height*0.55);ctx.lineTo(x+3*u,y);ctx.closePath();ctx.fill();
        }
        ctx.restore();break;
      }
      case "marker-ground":
        this.drawGroundMarker(sx0, sy0, u, d.marker);
        break;
      case "marker-invalid":
        this.drawInvalidMarker(sx0, sy0, u, d.marker);
        break;
    }
  }

  /** Flat diamond spanning the tile (top corner at the tile anchor). */
  private diamond(sx0: number, sy0: number, u: number, fill: string): void {
    const { ctx } = this;
    const hw = HALF_W * u;
    const hh = HALF_H * u;
    ctx.fillStyle = fill;
    ctx.beginPath();
    ctx.moveTo(sx0, sy0);
    ctx.lineTo(sx0 + hw, sy0 + hh);
    ctx.lineTo(sx0, sy0 + TILE_H * u);
    ctx.lineTo(sx0 - hw, sy0 + hh);
    ctx.closePath();
    ctx.fill();
  }

  /** Stroke-only diamond outline (hover highlight, matches the tile shape). */
  private diamondOutline(sx0: number, sy0: number, u: number, stroke: string, width: number): void {
    const { ctx } = this;
    const hw = HALF_W * u;
    const hh = HALF_H * u;
    ctx.strokeStyle = stroke;
    ctx.lineWidth = width;
    ctx.beginPath();
    ctx.moveTo(sx0, sy0);
    ctx.lineTo(sx0 + hw, sy0 + hh);
    ctx.lineTo(sx0, sy0 + TILE_H * u);
    ctx.lineTo(sx0 - hw, sy0 + hh);
    ctx.closePath();
    ctx.stroke();
  }

  /** One revealed tile: the room's floor tile (the host-resolved palette
   *  shade), then the terrain prop when the tile is an obstacle (wall/pond/
   *  table — the room recipe's furniture; the wall strip keeps its bare
   *  floor when it has no prop). */
  /**
   * Bake (or reuse) the offscreen holding every revealed FLOOR tile, and return
   * whether it is usable this frame.
   *
   * On a revealed floor the painter list fills ~1,000 tiles, and a floor is
   * pure static art: revealed mask + palette + camera, nothing per-frame. Baking
   * turns ~1,000 drawImage calls into ONE and moves the ~5 ms cost from every
   * frame to once per half-second of walking — the difference between a frame
   * budget of ~7 ms of JS and ~1 ms.
   *
   * The layer covers the draw area (the cull-padded viewport) anchored on the
   * camera, and is reused while the camera stays within half a tile of that
   * anchor. The reuse error is provably invisible: anything missing after a
   * small camera move lies inside the cull margin, which is off-screen by
   * construction (that is what CULL_MARGIN_* are for).
   *
   * Only FLOORS are baked. Obstacle art and the perimeter masonry stay in the
   * painter list keeping their depth interleaving with entities; hoisting the
   * floors is visually identical because a floor is flat and never overlaps
   * anything drawn after it.
   */
  private ensureFloorLayer(obs: Observation, u: number, mX: number, mY: number): boolean {
    const wa = this.worldArt;
    if (!wa) return false; // no atlas → the per-tile flat-diamond fallback draws
    const x0 = Math.round(this.camX - mX);
    const y0 = Math.round(this.camY - mY);
    const w = Math.round(this.cssW + 2 * mX);
    const h = Math.round(this.cssH + 2 * mY);
    // One canvas, one allocation: past ~16 Mpx, decline rather than risk an
    // allocation failure, and the floors draw per tile as they always did.
    if (w <= 0 || h <= 0 || w * h > 16_000_000) return false;
    const sig = `${this.layerSignature(obs)}|${this.floorPalette}|${this.architecture}|${this.wear}`;
    const stale =
      !this.floorLayer ||
      this.layerU !== u ||
      this.layerDpr !== this.dpr ||
      this.layerSig !== sig ||
      Math.abs(this.camX - this.layerCamX) > TILE_W * u * 0.5 ||
      Math.abs(this.camY - this.layerCamY) > TILE_H * u * 0.5;
    if (!stale) return true;

    let c = this.floorLayer;
    if (!c) {
      c = document.createElement("canvas");
      this.floorLayer = c;
    }
    const dw = Math.ceil(w * this.dpr);
    const dh = Math.ceil(h * this.dpr);
    if (c.width !== dw || c.height !== dh) {
      c.width = dw;
      c.height = dh;
    }
    const lc = c.getContext("2d");
    if (!lc) {
      this.floorLayer = null;
      return false;
    }
    lc.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    lc.clearRect(0, 0, w, h);
    const range = visibleTileRange(
      obs.width,
      obs.height,
      u,
      x0,
      y0,
      this.cssW + 2 * mX,
      this.cssH + 2 * mY,
    );
    if (range) {
      for (let ty = range.y0; ty <= range.y1; ty++) {
        for (let tx = range.x0; tx <= range.x1; tx++) {
          if (obs.revealed[ty * obs.width + tx] !== 1) continue;
          wa.drawFloorTile(
            lc,
            (tx - ty) * HALF_W * u - x0,
            (tx + ty) * HALF_H * u - y0,
            u,
            this.floorImg(this.floorFrame(tx, ty)),
          );
        }
      }
    }
    this.layerX0 = x0;
    this.layerY0 = y0;
    this.layerW = w;
    this.layerH = h;
    this.layerCamX = this.camX;
    this.layerCamY = this.camY;
    this.layerU = u;
    this.layerDpr = this.dpr;
    this.layerSig = sig;
    return true;
  }

  /** FNV-1a over the tiles the baked floors depend on. A few thousand integer
   *  ops, cheap enough to run every frame and exact: a reveal, a terrain change
   *  or a re-tuned palette repaints the layer, and a plain camera move or a
   *  tick with nothing changed does not. */
  private layerSignature(obs: Observation): number {
    let h = 2166136261;
    for (let i = 0; i < obs.revealed.length; i++) {
      h = Math.imul(h ^ (obs.revealed[i] ?? 0), 16777619);
      h = Math.imul(h ^ (obs.map[i] ?? 0), 16777619);
      h = Math.imul(h ^ (obs.terrain[i] ?? 0), 16777619);
    }
    return h >>> 0;
  }

  private drawTerrainTile(sx0: number, sy0: number, u: number, x: number, y: number, map: number, terrain: number): void {
    const wa = this.worldArt;
    if (!wa) {
      // No atlas yet: the flat diamond for every revealed tile (the M0–M2
      // playability contract) — obstacles are still readable as the map
      // overlay's wall bits and the hover readout.
      this.diamond(sx0, sy0, u, C_FLOOR_FALLBACK);
      return;
    }
    const floorImg = this.floorImg(this.floorFrame(x, y));
    if (map === 1 || terrain === TERRAIN_FLOOR) {
      wa.drawFloorTile(this.ctx, sx0, sy0, u, floorImg);
      return;
    }
    wa.drawTerrain(this.ctx, sx0, sy0, u, terrain, floorImg, terrain === TERRAIN_WALL ? this.wallFrame(x, y) : 0);
  }

  /** The current floor tile image (per-palette, cached — one lookup per
   *  palette, not one per tile). A NULL is never cached: the first frame can
   *  land while the tile is still decoding, and a cached null would keep the
   *  floor on the flat fallback for the whole session (the arena caught this).
   *  An errored tile re-queries every call — a Map lookup + a `complete`
   *  check, not a load. */
  private floorImgCache = new Map<string, HTMLImageElement | null>();
  private floorImg(frame: number): HTMLImageElement | null {
    const wa = this.worldArt;
    if (!wa) return null;
    const key = `${this.floorPalette}:${frame}`;
    const cached = this.floorImgCache.get(key);
    // A cached element is only reusable while it still HOLDS a bitmap: the
    // sprite store's LRU can evict the pack entry underneath it, and an evicted
    // element reports naturalWidth 0 forever. Caching that as "the tile" keeps
    // painting nothing for the rest of the session, so a dead entry is dropped
    // and re-queried (the store re-decodes it from the pack).
    if (cached && cached.complete && cached.naturalWidth > 0) return cached;
    if (cached !== undefined) this.floorImgCache.delete(key);
    const im = wa.floorTile(this.floorPalette, frame);
    if (im) this.floorImgCache.set(key, im);
    return im;
  }

  /** Stable, coordinate-only cosmetic randomisation lives in worldArt
   *  (`tileHash`/`floorFrame`) so the sign-in scene's town floor wears
   *  identically to this one — one implementation, no second approximation. */
  private floorFrame(x: number, y: number): number {
    return civicFloorFrame(this.architecture, this.wear, x, y);
  }

  /** The 4-bit connected-wall mask is local to the revealed observation.
   * We only inspect adjacent revealed terrain—unknown rooms remain void. */
  private wallFrame(x: number, y: number): number {
    if (this.architecture !== "civic-stone-v1") return 0;
    const at = (tx: number, ty: number): boolean => {
      const obs = this.lastObservation;
      if (!obs || tx < 0 || ty < 0 || tx >= obs.width || ty >= obs.height) return false;
      const i = ty * obs.width + tx;
      return obs.revealed[i] === 1 && obs.terrain[i] === TERRAIN_WALL;
    };
    return (at(x, y - 1) ? 1 : 0) | (at(x + 1, y) ? 2 : 0) | (at(x, y + 1) ? 4 : 0) | (at(x - 1, y) ? 8 : 0);
  }

  /** The perimeter ring tile: the strip's own wall prop at its frame (no
   *  floor underneath — the strip is void in the observation; the block is
   *  the whole drawable). */
  private drawWallRing(sx0: number, sy0: number, u: number, mask: number): void {
    const wa = this.worldArt;
    if (!wa) return;
    wa.drawAnchored(this.ctx, sx0, sy0 + HALF_H * u, u, PROP_WALL, mask);
  }

  /** Whether an (unrevealed or out-of-bounds) tile carries a perimeter ring
   *  wall: directly outside the revealed room block on the strip row — i.e.
   *  adjacent to a revealed tile that is NOT a door slot (a slot's far side
   *  is the next room's floor, void until revealed), or the diagonal corner
   *  tile completing a convex room corner (two ring runs meeting at a
   *  revealed floor corner — the strip's own corner tile in the sim). */
  private isRingWall(x: number, y: number, doorSlots: Set<number>): boolean {
    const o = this.lastObservation;
    if (!o) return false;
    const revealedTile = (tx: number, ty: number): boolean => {
      if (tx < 0 || ty < 0 || tx >= o.width || ty >= o.height) return false;
      const i = ty * o.width + tx;
      return o.revealed[i] === 1 && !doorSlots.has(i);
    };
    const baseRing = (tx: number, ty: number): boolean =>
      revealedTile(tx, ty - 1) || revealedTile(tx + 1, ty) || revealedTile(tx, ty + 1) || revealedTile(tx - 1, ty);
    if (baseRing(x, y)) return true;
    // Convex corner fill: the diagonal outside a revealed floor corner, with
    // both orthogonal neighbours already ring tiles (the strip corner).
    const corner = (fx: number, fy: number, ax: number, ay: number, bx: number, by: number): boolean =>
      revealedTile(fx, fy) && baseRing(ax, ay) && baseRing(bx, by);
    return (
      corner(x + 1, y - 1, x, y - 1, x + 1, y) ||
      corner(x - 1, y - 1, x, y - 1, x - 1, y) ||
      corner(x + 1, y + 1, x, y + 1, x + 1, y) ||
      corner(x - 1, y + 1, x, y + 1, x - 1, y)
    );
  }

  /** The ring tile's 4-bit join mask (the wall prop's frame index): a
   *  direction is "joined" when that neighbour is also wall — another ring
   *  tile or a revealed in-room wall (the terrain pass draws it; same
   *  height, same courses, the run continues). A revealed floor or a door
   *  slot is an opening: the edge stays exposed (rim + course end). Same
   *  convention as wallFrame for the revealed terrain walls. */
  private ringMask(x: number, y: number, doorSlots: Set<number>): number {
    const o = this.lastObservation;
    if (!o) return 0;
    const wallish = (tx: number, ty: number): boolean => {
      if (tx < 0 || ty < 0 || tx >= o.width || ty >= o.height) {
        return this.isRingWall(tx, ty, doorSlots);
      }
      const i = ty * o.width + tx;
      if (o.revealed[i] === 1) return o.terrain[i] === TERRAIN_WALL && !doorSlots.has(i);
      return this.isRingWall(tx, ty, doorSlots);
    };
    return (wallish(x, y - 1) ? 1 : 0) | (wallish(x + 1, y) ? 2 : 0) | (wallish(x, y + 1) ? 4 : 0) | (wallish(x - 1, y) ? 8 : 0);
  }

  /**
   * The door's state art in the wall-strip gap (the door tile's floor
   * diamond is drawn by the terrain pass underneath). Closed = the stone
   * block with its wood slab; open = the worn threshold. The key letter
   * stays a small RUNTIME overlay (never baked into the art — one asset
   * serves every door); the "D" is gone.
   */
  private drawDoor(sx0: number, sy0: number, u: number, open: boolean, keyId: number, guarded = false, puzzleLocked = false): void {
    const wa = this.worldArt;
    if (wa) wa.drawDoor(this.ctx, sx0, sy0, u, open);
    if (!open) {
      // Closed state: a key door shows its key letter; a GUARDIAN door
      // (design/guardian-door.md) shows the ward mark instead — a visibly
      // different look, so it never reads as "needs a key" (no key exists).
      const mx = sx0 + TILE_W * u * 0.2;
      const my = sy0 + TILE_H * u * 0.45;
      if (puzzleLocked) this.label(mx, my, u, "◇", "#ba9ce3", 0.3);
      else if (guarded) this.drawGuardWard(mx, my, u);
      else this.label(mx, my, u, keyLetter(keyId), C_KEY, 0.3);
    }
  }

  /**
   * The guardian door's ward mark: a runtime-drawn red seal (a diamond with
   * a crossbar) in the key letter's spot. No new sprites for MVP — the
   * same "distinguished by a runtime mark alone" approach as the two
   * floor-end portals; the slab art underneath is the shared closed door.
   */
  private drawGuardWard(cx: number, cy: number, u: number): void {
    const { ctx } = this;
    const s = 0.14 * TILE_W * u; // half-diagonal of the diamond
    ctx.strokeStyle = C_GUARD;
    ctx.lineWidth = Math.max(1, 1.5 * u);
    ctx.beginPath();
    ctx.moveTo(cx, cy - s);
    ctx.lineTo(cx + s, cy);
    ctx.lineTo(cx, cy + s);
    ctx.lineTo(cx - s, cy);
    ctx.closePath();
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(cx - s * 0.7, cy);
    ctx.lineTo(cx + s * 0.7, cy);
    ctx.stroke();
  }

  /** M4 dropped key icon lying on its tile (the re-authored canon gold key),
   *  with the key letter overlaid at render time (the letter is never baked
   *  into the art — one asset serves every door key). */
  private drawKey(sx0: number, sy0: number, u: number, id: number): void {
    const cx = sx0;
    const cy = sy0 + HALF_H * u;
    const assets = this.heroAssets;
    const im = assets ? itemFrameImg(assets, "key", "canon") : null;
    if (im) {
      // Lying-flat dropped item at the tile diamond center: the atlas anchor
      // (24,48) is the item's ground point. Contact shadow drawn at render
      // time — never baked into the PNG (the same art is the inventory icon).
      this.drawItem(im, cx, cy, u);
    }
    this.label(cx, cy - 4 * u, u, keyLetter(id), C_KEY, 0.4);
  }

  /** A ground DROP (design/loot.md): the item icon lying flat on its tile
   *  (the same 48×48 substrate art as the inventory cell — the icon id is
   *  already shade-baked, so canon tint, exactly like the HUD). */
  private drawDrop(sx0: number, sy0: number, u: number, d: { cls: "equipment" | "consumable"; itemKind: number }): void {
    const cx = sx0;
    const cy = sy0 + HALF_H * u;
    const iconId = d.cls === "equipment" ? itemByKind(d.itemKind)?.icon : consumableById(d.itemKind)?.icon;
    const im = this.heroAssets && iconId ? itemFrameImg(this.heroAssets, iconId, "canon") : null;
    if (im) {
      this.drawItem(im, cx, cy, u);
    }
  }

  /** Interact outline for a drop: the real item silhouette when its committed
   *  atlas frame is ready. Missing content stays absent; the interaction
   *  marker itself still comes from the authoritative object. */
  private drawDropOutline(
    sx0: number,
    sy0: number,
    u: number,
    d: { cls: "equipment" | "consumable"; itemKind: number },
    m: Marker | undefined,
  ): void {
    const style = this.outlineStyle(m);
    if (!style) return;
    const cx = sx0;
    const cy = sy0 + HALF_H * u;
    const iconId = d.cls === "equipment" ? itemByKind(d.itemKind)?.icon : consumableById(d.itemKind)?.icon;
    const im = this.heroAssets && iconId ? itemFrameImg(this.heroAssets, iconId, "canon") : null;
    if (im) {
      this.outlineItem(im, cx, cy, u, style);
    }
  }

  /** The base-room SACK (design/loot.md): a canvas-drawn leather pouch —
   *  no committed art for it yet (the substrate is tile/item art; a new
   *  committed shape is a pipeline decision, not a render one). The shape
   *  reads at a glance: a rounded body with a cinched neck, slightly
   *  larger than a lying item so it registers as furniture. */
  private drawSack(sx0: number, sy0: number, u: number): void {
    const cx = sx0;
    const cy = sy0 + HALF_H * u;
    const { ctx } = this;
    const s = u * (TILE_H / 2); // one unit of the tile's half-height
    // Contact shadow (same footprint discipline as drawItem).
    ctx.fillStyle = "rgba(0, 0, 0, 0.35)";
    ctx.beginPath();
    ctx.ellipse(cx, cy + 0.62 * s, 0.52 * s, 0.2 * s, 0, 0, Math.PI * 2);
    ctx.fill();
    // Body: a rounded pouch (two quadratic curves), leather brown.
    ctx.fillStyle = "#8a6238";
    ctx.beginPath();
    ctx.moveTo(cx - 0.18 * s, cy - 0.42 * s); // neck left
    ctx.quadraticCurveTo(cx - 0.55 * s, cy - 0.1 * s, cx - 0.42 * s, cy + 0.34 * s); // belly left
    ctx.quadraticCurveTo(cx, cy + 0.62 * s, cx + 0.42 * s, cy + 0.34 * s); // bottom
    ctx.quadraticCurveTo(cx + 0.55 * s, cy - 0.1 * s, cx + 0.18 * s, cy - 0.42 * s); // belly right
    ctx.closePath();
    ctx.fill();
    // Shading: a darker lower half (a single flat pass — the art canon is
    // flat + one shadow, no gradients on the floor).
    ctx.fillStyle = "rgba(0, 0, 0, 0.22)";
    ctx.beginPath();
    ctx.moveTo(cx - 0.46 * s, cy + 0.12 * s);
    ctx.quadraticCurveTo(cx, cy + 0.5 * s, cx + 0.46 * s, cy + 0.12 * s);
    ctx.quadraticCurveTo(cx, cy + 0.72 * s, cx - 0.46 * s, cy + 0.12 * s);
    ctx.closePath();
    ctx.fill();
    // The cinch: a dark band across the neck + a small tie knot.
    ctx.strokeStyle = "#4a331c";
    ctx.lineWidth = Math.max(1, 0.1 * s);
    ctx.beginPath();
    ctx.moveTo(cx - 0.2 * s, cy - 0.38 * s);
    ctx.quadraticCurveTo(cx, cy - 0.3 * s, cx + 0.2 * s, cy - 0.38 * s);
    ctx.stroke();
    ctx.fillStyle = "#4a331c";
    ctx.beginPath();
    ctx.arc(cx, cy - 0.36 * s, 0.09 * s, 0, Math.PI * 2);
    ctx.fill();
    // The mouth above the cinch.
    ctx.fillStyle = "#5d4226";
    ctx.beginPath();
    ctx.ellipse(cx, cy - 0.42 * s, 0.18 * s, 0.07 * s, 0, 0, Math.PI * 2);
    ctx.fill();
  }

  /** Interact outline for the sack: a fixed tile diamond (the pouch is
   *  canvas-drawn, so there is no silhouette to trace — the diamond matches
   *  its footprint). */
  private drawSackOutline(sx0: number, sy0: number, u: number, m: Marker | undefined): void {
    const style = this.outlineStyle(m);
    if (!style) return;
    this.diamondOutline(sx0, sy0, u, hexWithAlpha(MARKER_CONFIG.interactColor, style.alpha), 2);
  }

  /** M4 dropped gatestone radiance (drawn UNDER the interact outline so the
   *  cyan ring reads clearly — the glow would otherwise tint it). Each stone
   *  radiates its colour: gold for the group stone, the owner's identity
   *  colour for a personal stone. */
  private drawStoneGlow(sx0: number, sy0: number, u: number, kind: "personal" | "group", ownerId: number): void {
    const cx = sx0;
    const cy = sy0 + HALF_H * u;
    const { ctx } = this;
    const glow = (color: (a: number) => string, r: number): void => {
      const g = ctx.createRadialGradient(cx, cy, 0, cx, cy, r);
      g.addColorStop(0, color(0.85));
      g.addColorStop(0.35, color(0.32));
      g.addColorStop(1, color(0));
      ctx.fillStyle = g;
      ctx.fillRect(cx - r, cy - r, r * 2, r * 2);
    };
    if (kind === "group") glow((a) => `rgba(217, 164, 65, ${a})`, TILE_H * u * 0.95);
    else glow((a) => playerGlow(ownerId, a), TILE_H * u * 0.8);
  }

  /** M4 dropped gatestone icon (PGS tinted by the owner's identity colour;
   *  GGS canon gold). The glow + interact outline keep it findable; missing
   *  atlas = draws nothing (no placeholder circle). */
  private drawStoneSprite(sx0: number, sy0: number, u: number, kind: "personal" | "group", ownerId: number): void {
    const cx = sx0;
    const cy = sy0 + HALF_H * u;
    const assets = this.heroAssets;
    const im =
      assets && kind === "group"
        ? itemFrameImg(assets, "gatestone-group", "canon")
        : assets
          ? itemFrameImg(assets, "gatestone-personal", playerTint(ownerId))
          : null;
    if (im) this.drawItem(im, cx, cy, u);
  }

  /** Draw a 48×48 item sprite as a ground-plane object at the tile diamond
   *  center. The frame is compressed to the 2:1 floor plane and centred on
   *  the contact point; using the frame bottom as a billboard anchor made
   *  piles appear to hover above the tile. */
  private drawItem(im: HTMLImageElement, cx: number, cy: number, u: number): void {
    const { ctx } = this;
    // Logical 48×48 item space → css: half of it per tile scale u. A ground
    // plane is 5:8 high in this projection, so the square icon becomes a
    // compact, grounded heap instead of an upright card.
    const w = im.naturalWidth * 0.5 * u;
    const h = im.naturalHeight * 0.3125 * u;
    this.worldArt?.drawContactShadow(ctx, cx, cy + 1.2 * u, u, w * 0.42, h * 0.16, 0.32);
    ctx.drawImage(im, cx - w / 2, cy - h / 2, w, h);
  }

  /**
   * Player: the M2 hero sprite billboard at the tile diamond center (feet
   * anchored at the atlas anchor), tinted per player, with a name tag above
   * the head and a white ring under the local player. When the atlas has no
   * frame yet, draw nothing (no placeholder card — it clipped under the hero).
   */
  /** Death-bones ground decal (arena). The 96 px pile is drawn SQUASHED onto
   *  the iso ground plane (h = 0.625·w — the 2:1 projection of a pile lying
   *  flat) and centred on the death tile: it reads as ON the floor (not a
   *  floating billboard) and stays inside its tile. The painter sort puts it
   *  one row up + terrain-first, so standing combatants always draw over it. */
  private drawBonesDecal(sx0: number, sy0: number, u: number, d: Extract<Obj, { obj: "bones" }>): void {
    const im = this.bonesImg;
    if (!im) return; // the first frame(s) while the asset loads
    const alpha = Math.min(1, (this._now - (this.bonesBirth.get(`${d.who}${d.id}`) ?? this._now)) / 540);
    if (alpha <= 0) return;
    const w = 32 * u;
    const h = w * 0.625;
    const ctx = this.ctx;
    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.imageSmoothingEnabled = true; // a smooth 3D prop, not pixel art
    ctx.drawImage(im, sx0 - w / 2, sy0 - h / 2, w, h);
    ctx.restore();
  }

  /**
   * A player's effect visuals for THIS frame: Bound, Sprint strength (plus
   * the trail's upkeep) and the Blink ghost/fade, all read from the
   * observation + the retained effect frame. Pure cosmetics: nothing here
   * feeds back into anything (AGENTS §6) — the sim owns every position.
   */
  private playerFx(p: ObservedPlayer, pos: Vec2, now: number, tick: number | undefined): PlayerFx | undefined {
    let sprint = 0;
    if (p.speedBuff) {
      // Ease down over the buff's last SPRINT_FADE_TICKS so the fading red IS
      // the duration readout. A host with no tick (unused today) shows full.
      const remaining = tick === undefined ? SPRINT_FADE_TICKS : Math.max(0, p.speedBuff.until - tick);
      sprint = remaining >= SPRINT_FADE_TICKS ? 1 : 0.35 + 0.65 * (remaining / SPRINT_FADE_TICKS);
      // The trail: sample the RENDERED position (the sprite's, not the tile's)
      // at most every SPRINT_SAMPLE_MS and keep the last SPRINT_TRAIL_MS.
      const trail = this.sprintTrail.get(p.id) ?? [];
      const last = trail[trail.length - 1];
      if (!last || now - last.at >= SPRINT_SAMPLE_MS) trail.push({ x: pos.x, y: pos.y, at: now });
      while (trail.length > 0 && now - trail[0]!.at > SPRINT_TRAIL_MS) trail.shift();
      this.sprintTrail.set(p.id, trail);
    } else if (this.sprintTrail.size > 0) {
      this.sprintTrail.delete(p.id); // buff over: no stale ghosts
    }
    const blink = this.effects?.blinks.find((b) => b.playerId === p.id);
    const bound = isBound(p, tick);
    if (sprint <= 0 && !blink && !bound) return undefined;
    return {
      bound,
      sprint,
      blinkFade: blink ? Math.min(1, 0.25 + 0.75 * ((now - blink.at) / BLINK_FADE_MS)) : 1,
      blinkFrom: blink
        ? { x: blink.fromX, y: blink.fromY, alpha: Math.max(0, 1 - (now - blink.at) / BLINK_MS) }
        : undefined,
      phoenix:
        blink?.fx === "phoenix" ? Math.max(0, 1 - (now - blink.at) / BLINK_MS) : undefined,
    };
  }

  /**
   * The Phoenix Teleport's arrival (a `blink` event stamped `fx: "phoenix"`):
   * a hot ring on the landing tile with three flames lifting out of it, all
   * fading over the blink's life. The counterpart to the departure ghost —
   * the Squire's plain Blink never draws it. Pure cosmetics at the hero's
   * RENDERED position (AGENTS §6): the sim owns the tile and the tick.
   */
  private drawPhoenixFlare(ctx: CanvasRenderingContext2D, cx: number, cy: number, u: number, alpha: number): void {
    const a = Math.max(0, Math.min(1, alpha));
    if (a <= 0) return;
    ctx.save();
    ctx.strokeStyle = `rgba(255,150,58,${(0.85 * a).toFixed(3)})`;
    ctx.lineWidth = Math.max(1, 2 * u);
    ctx.beginPath();
    ctx.ellipse(cx, cy + 3 * u, 17 * u, 6 * u, 0, 0, Math.PI * 2);
    ctx.stroke();
    ctx.strokeStyle = `rgba(255,206,120,${(0.75 * a).toFixed(3)})`;
    ctx.lineWidth = Math.max(1, 1.5 * u);
    for (const [dx, h] of [[-8, 10], [0, 16], [8, 10]] as const) {
      ctx.beginPath();
      ctx.moveTo(cx + dx * u, cy + 2 * u);
      ctx.quadraticCurveTo(cx + (dx + 3) * u, cy - h * 0.5 * u, cx + dx * u, cy - h * u);
      ctx.stroke();
    }
    ctx.restore();
  }

  /**
   * The Sprint ground ring: a soft red ellipse at the hero's feet (outside
   * the white "you" ring when this is the local player) with a gentle
   * breathing pulse. Wall-clock cosmetics — never a sim input.
   */
  private drawSprintRing(ctx: CanvasRenderingContext2D, cx: number, cy: number, u: number, strength: number): void {
    const pulse = 0.5 + 0.5 * Math.sin(this._now / 170);
    const r = (16 + 1.6 * pulse) * u;
    const y = cy + 2 * u;
    ctx.save();
    const g = ctx.createRadialGradient(cx, y, r * 0.35, cx, y, r * 1.55);
    g.addColorStop(0, "rgba(255,74,61,0)");
    g.addColorStop(0.75, `rgba(255,74,61,${0.22 * strength})`);
    g.addColorStop(1, "rgba(255,74,61,0)");
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.ellipse(cx, y, r * 1.55, r * 0.62, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = `rgba(255,120,92,${(0.55 + 0.25 * pulse) * strength})`;
    ctx.lineWidth = Math.max(1.2, 2 * u);
    ctx.beginPath();
    ctx.ellipse(cx, y, r, r * 0.42, 0, 0, Math.PI * 2);
    ctx.stroke();
    ctx.restore();
  }

  private drawPlayerSprite(
    sx0: number,
    sy0: number,
    u: number,
    id: number,
    name: string,
    meId: number,
    anim: PlayerAnim,
    equipment: Record<EquipSlot, number | null>,
    look: PlayerLook | null,
    deathT?: number,
    spin?: number,
    glowKind?: "enhanced" | "flourish" | "chain",
    immune?: boolean,
    reflecting?: boolean,
    fx?: PlayerFx,
  ): void {
    const assets = this.heroAssets;
    if (!assets) return;
    const { ctx } = this;
    const cx = sx0;
    const cy = sy0 + HALF_H * u; // tile diamond center = feet anchor point
    ctx.imageSmoothingEnabled = false;
    // The shared contact shadow (lab parity — the hero was the only mover
    // without one; it grounds the sprite on the tile).
    this.worldArt?.drawContactShadow(ctx, cx, cy + 2 * u, u, 14, 5.5);
    const [dw, dh] = assets.hero.dims;
    const [aw, ah] = assets.hero.anchor;
    // With a saved look the LUT IS the colour: canon frames underneath, and
    // the identity tint is skipped (the look's swatches replaced it). Without
    // one this is the historical baked-tint path, untouched.
    const tint = look ? "canon" : tintForPlayer(assets.hero.tints, id);
    const animName = anim.attackFrame !== undefined ? "attack" : anim.walkDist > 0 ? "walk" : "idle";
    const frame = anim.attackFrame !== undefined ? anim.attackFrame : anim.walkDist > 0 ? walkFrame(anim.walkDist) : idleFrame(anim.idleT);
    // The death animation (deathT ∈ [0,1)): phase 1 keels the hero over —
    // an ease-out rotation around the feet anchor; phase 2 fades it out.
    // Canvas-primitive animation (no pipeline frames needed).
    const dying = deathT !== undefined ? deathT : -1;
    if (dying >= 0) {
      ctx.save();
      const fallT = Math.min(1, dying / 0.55);
      const fall = fallT * (2 - fallT); // ease-out quad
      ctx.globalAlpha = dying < 0.6 ? 1 : Math.max(0, 1 - (dying - 0.6) / 0.4);
      ctx.translate(cx, cy);
      ctx.rotate((-fall * Math.PI) / 2);
      ctx.translate(-cx, -cy);
    }
    // The CHARGE GLOW (a pending Enhanced Strike / Flourish, or a charged
    // swing in flight): a soft aura around the body + a ring under the feet —
    // gold for enhanced, pale steel for flourish. It reads LOUD (the charge
    // is the exciting state), drawn under the composite. `spin` (radians) lets
    // the Flourish ring expand outward once over the swing (the 360° sweep).
    if (glowKind) this.drawChargeGlow(ctx, cx, cy, u, glowKind, spin ?? 0);
    // The Flourish SPIN: a full 360° TURN of the hero around the VERTICAL
    // axis (a spin in place, not a somersault). A screen-plane rotate read as
    // a flip/somersault, so instead the hero's FACING is cycled through the 8
    // directions over the charged swing — front → right → back → left → front.
    // The hero always shows a valid facing (never an edge-on line); the
    // expanding pale-steel ring (drawChargeGlow) sells the 360° sweep. Mutually
    // exclusive with the death fall (a dying hero doesn't swing).
    const spinning = spin !== undefined && spin > 0 && dying < 0;
    const renderDir = spinning
      ? (DIRS_CW[(Math.max(0, DIRS_CW.indexOf(anim.dir)) + Math.floor((spin / (Math.PI * 2)) * 8)) % 8] ?? anim.dir)
      : anim.dir;
    // The hero's resolved outfit (the v6 part pipeline): used by the Squire's
    // effect pass AND the hero draw below, so it is resolved once.
    const outfit = assets.hero.outfit
      ? look
        ? outfitForEquipment(assets.hero, equipment, ITEMS, outfitForLook(assets.hero, look.outfit))
        : outfitForEquipment(assets.hero, equipment)
      : null;
    // Status and class silhouettes (Bound / Sprint / Blink) need a
    // WHOLE-BODY composite: the hero is stacked part frames, and outlining
    // the parts individually would trace every layer seam. Built only when
    // there is something to draw, and drawn BEFORE the hero so the body
    // covers the inner half of the silhouette, leaving a colored edge.
    if (fx && outfit && (fx.sprint > 0 || fx.blinkFrom || fx.bound)) {
      const lut = look ? look.lut : null;
      const composite = heroComposite(assets, tint, outfit, animName, renderDir, frame, lut);
      if (composite) {
        const [cw, ch] = composite.dims;
        const [ca, cb] = composite.anchor;
        const poseKey = heroCompositeKey(assets, tint, outfit, animName, renderDir, frame, lut);
        if (fx.bound && dying < 0) {
          // Follow the complete current outfit/pose at the current position,
          // including after a teleport. No ghost and no fade near expiry.
          for (const pass of ICY_SILHOUETTE_PASSES) {
            const rim = pass.dilation * u;
            const sil = silhouetteForKey(composite.canvas, `${poseKey}|${pass.dilation}|${pass.color}`, cw, ch, pass.dilation, pass.color);
            ctx.save();
            ctx.globalAlpha = pass.alpha;
            ctx.drawImage(sil, cx - ca * u - rim, cy - cb * u - rim, cw * u + rim * 2, ch * u + rim * 2);
            ctx.restore();
          }
        }
        if (fx.sprint > 0 || fx.blinkFrom) {
          const rim = Math.max(1, SPRINT_RIM_PX * u);
          // Baked once per pose, not once per frame: the composite is cached (and
          // therefore a stable identity), so the dilation gets the same key. This
          // pass used to allocate a fresh 16-sample dilated canvas per sprinting
          // hero per frame.
          const d = rim / u;
          const sil = silhouetteForKey(
            composite.canvas,
            `${poseKey}|${d}|${SPRINT_RED}`,
            cw,
            ch,
            d,
            SPRINT_RED,
          );
          const blit = (gx: number, gy: number, alpha: number): void => {
            ctx.save();
            ctx.globalAlpha = Math.max(0, Math.min(1, alpha));
            ctx.drawImage(sil, gx - ca * u - rim, gy - cb * u - rim, cw * u + rim * 2, ch * u + rim * 2);
            ctx.restore();
          };
          // Order: the ground ring, the Blink's departure ghost, the Sprint
          // trail (oldest first), then the outline on the hero itself.
          if (fx.sprint > 0) this.drawSprintRing(ctx, cx, cy, u, fx.sprint);
          if (fx.blinkFrom) {
            const gx = (fx.blinkFrom.x - fx.blinkFrom.y) * HALF_W * u;
            const gy = (fx.blinkFrom.x + fx.blinkFrom.y) * HALF_H * u + HALF_H * u;
            blit(gx, gy, fx.blinkFrom.alpha * 0.85);
          }
          if (fx.sprint > 0) {
            for (const t of this.sprintTrail.get(id) ?? []) {
              const age = (this._now - t.at) / SPRINT_TRAIL_MS;
              if (age <= 0.02) continue; // the hero's own spot — the body covers it
              const gx = (t.x - t.y) * HALF_W * u;
              const gy = (t.x + t.y) * HALF_H * u + HALF_H * u;
              blit(gx, gy, (1 - age) * 0.4 * fx.sprint);
            }
            blit(cx, cy, 0.9 * fx.sprint);
          }
        }
      }
    }
    // The Blink arrival: the hero fades in over BLINK_FADE_MS (the mirrored
    // half of the departure ghost). Never predicted — the event is the sim's.
    const heroAlpha = fx && fx.blinkFade < 1 ? fx.blinkFade : 1;
    if (heroAlpha < 1) {
      ctx.save();
      ctx.globalAlpha = heroAlpha;
    }
    if (outfit) {
      drawHero(ctx, assets, tint, outfit, animName, renderDir, frame, cx, cy, u, look ? look.lut : null);
    } else {
      // legacy v5 baked composites (kept for atlas back-compat)
      const im = heroFrameImg(assets, tint, animName, renderDir, frame);
      if (im) {
        if (fx?.bound && dying < 0) {
          for (const pass of ICY_SILHOUETTE_PASSES) {
            outlineSprite(ctx, { image: im }, cx - aw * u, cy - ah * u, dw * u, dh * u, pass.dilation * u, pass.color, pass.alpha);
          }
        }
        ctx.drawImage(im, cx - aw * u, cy - ah * u, dw * u, dh * u);
      }
    }
    if (heroAlpha < 1) ctx.restore();
    if (dying >= 0) {
      // A falling corpse gets no me-ring/name tag (the transform is also
      // balanced here — never leak a save/restore mid-pass).
      ctx.restore();
      return;
    }
    // The Barricade SHELL: a stone shell wrapping the hero while the damage-
    // immune window holds (drawn after the composite so it wraps the body).
    if (immune) this.drawBarricadeShell(ctx, cx, cy, u);
    if (reflecting) this.drawReflectShell(ctx, cx, cy, u);
    if (fx?.phoenix) this.drawPhoenixFlare(ctx, cx, cy, u, fx.phoenix);
    if (id === meId) {
      ctx.strokeStyle = "rgba(255,255,255,0.85)";
      ctx.lineWidth = Math.max(1, 1.5 * u);
      ctx.beginPath();
      ctx.ellipse(cx, cy + 3 * u, 15 * u, 5 * u, 0, 0, Math.PI * 2);
      ctx.stroke();
    }
    this.nameTag(cx, cy - ah * u - 9 * u, u, name, playerColor(id));
  }

  /** The charge glow (a pending or in-flight ability charge): a soft radial
   *  aura around the mid-body + a ring under the feet. Gold for an Enhanced
   *  Strike charge, pale steel for a Flourish charge. Pulses gently — the
   *  wall-clock is cosmetic-only (AGENTS §6), never a sim input. */
  private drawChargeGlow(ctx: CanvasRenderingContext2D, cx: number, cy: number, u: number, kind: "enhanced" | "flourish" | "chain", spin = 0): void {
    const pulse = 0.5 + 0.5 * Math.sin(this._now / 140);
    const [core, mid] =
      kind === "enhanced"
        ? ["rgba(255,224,138,", "rgba(255,196,64,"]
        : ["rgba(234,246,255,", "rgba(150,205,255,"]; // flourish + chain share the pale-steel look
    // t = 0..1 progress over the charged swing's spin (0 = a pending charge
    // that hasn't started swinging yet).
    const t = Math.max(0, Math.min(1, spin / (Math.PI * 2)));
    ctx.save();
    // The under-feet ring. A PENDING charge (t==0) is a steady armed ring;
    // a swing in flight (t>0) EXPANDS outward once over the spin and fades —
    // the clearest "360° sweep" read (an expanding ground circle).
    const ringR = (16 + (t > 0 ? t * 22 : pulse * 3)) * u;
    ctx.globalAlpha = t > 0 ? Math.max(0, 0.9 - t * 0.85) : 0.5 + pulse * 0.3;
    ctx.strokeStyle = `${mid}0.9)`;
    ctx.lineWidth = Math.max(1.5, (t > 0 ? 2.5 + t * 1.5 : 2.5) * u);
    ctx.beginPath();
    ctx.ellipse(cx, cy + 2 * u, ringR, ringR * 0.42, 0, 0, Math.PI * 2);
    ctx.stroke();
    // The body aura — kept SMALL and soft so the (rotating) hero stays the
    // readable centre of the effect, never a blob that swallows it.
    const ay = cy - 24 * u;
    const ar = (13 + pulse * 2) * u;
    const g = ctx.createRadialGradient(cx, ay, 0, cx, ay, ar);
    g.addColorStop(0, `${core}${t > 0 ? 0.3 : 0.5})`);
    g.addColorStop(0.5, `${mid}${t > 0 ? 0.16 : 0.28})`);
    g.addColorStop(1, `${mid}0)`);
    ctx.globalAlpha = 1;
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.arc(cx, ay, ar, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  /** The Barricade shell: a translucent stone "bubble" wrapping the hero while
   *  the damage-immune window holds (a slow pulse sells "active defense").
   *  Stone palette so it reads as a barrier, never a heal glow. */
  private drawBarricadeShell(ctx: CanvasRenderingContext2D, cx: number, cy: number, u: number): void {
    const pulse = 0.5 + 0.5 * Math.sin(this._now / 260);
    const bw = 24 * u; // half-width
    const bh = 40 * u; // half-height (wraps the ~96-px body)
    ctx.save();
    const g = ctx.createRadialGradient(cx, cy - bh * 0.5, bh * 0.2, cx, cy - bh * 0.5, bh);
    g.addColorStop(0, `rgba(210,205,190,${0.1 + pulse * 0.05})`);
    g.addColorStop(1, `rgba(150,145,130,${0.22 + pulse * 0.1})`);
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.ellipse(cx, cy - bh * 0.5, bw, bh, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.globalAlpha = 0.55 + pulse * 0.35;
    ctx.strokeStyle = "rgba(226,220,205,0.9)";
    ctx.lineWidth = Math.max(1.5, 2.5 * u);
    ctx.beginPath();
    ctx.ellipse(cx, cy - bh * 0.5, bw, bh, 0, 0, Math.PI * 2);
    ctx.stroke();
    ctx.restore();
  }

  /** Violet glass and a returning arrow distinguish Reflect from stone Barricade. */
  private drawReflectShell(ctx: CanvasRenderingContext2D, cx: number, cy: number, u: number): void {
    const pulse = 0.5 + 0.5 * Math.sin(this._now / 230);
    ctx.save();
    ctx.fillStyle = `rgba(116,82,210,${0.11 + pulse * 0.07})`;
    ctx.strokeStyle = `rgba(200,160,255,${0.7 + pulse * 0.25})`;
    ctx.lineWidth = Math.max(1.5, 2.5 * u);
    ctx.beginPath();
    ctx.ellipse(cx, cy - 20 * u, 24 * u, 40 * u, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
    ctx.restore();
  }

  /** An enemy (NPC) billboard: the atlas.npcs[family] frame at the tile diamond
   *  centre (feet anchor) with a render-time contact shadow, driven by the same
   *  anim model as the hero (attack frame when swinging, else walk/idle). The
   *  atlas is keyed by SPRITE FAMILY (the art); the kind's `tint` picks the
   *  frame set within it (a shaded NPC like goblin-ff2600 renders
   *  atlas.npcs.goblin's ff2600 frames). Draws nothing when the atlas has no
   *  frames for the family. */
  private drawEnemySprite(sx0: number, sy0: number, u: number, npcKind: string, tint: string, anim: PlayerAnim): void {
    const assets = this.heroAssets;
    const npc = assets?.atlas.npcs?.[enemyByKind(npcKind)?.family ?? npcKind];
    if (!npc) return;
    const { ctx } = this;
    const cx = sx0;
    const cy = sy0 + HALF_H * u; // feet anchor = tile diamond centre
    // the contact shadow scales with the creature's footprint (the 256px
    // dragon boss is ~2.7x the 96px contract cell — a fixed 16px shadow
    // would sit under its feet like a coin). Floaters (the ghost) are
    // not in ground contact: fainter + smaller.
    const k = Math.max(1, npc.dims[0] / 96);
    this.worldArt?.drawContactShadow(
      ctx, cx, cy + 2 * u, u,
      (npc.floats ? 12 : 16) * k, (npc.floats ? 5 : 7) * k,
      npc.floats ? 0.18 : 0.38,
    );
    const spriteY = cy - (anim.pounceHeight ?? 0) * u;
    ctx.imageSmoothingEnabled = false;
    const animName = anim.summonFrame !== undefined ? npc.anims.summon ? "summon" : "attack" : anim.pounceFrame !== undefined ? npc.anims.leap ? "leap" : "attack" : anim.attackFrame !== undefined ? "attack" : anim.walkDist > 0 ? "walk" : "idle";
    const frame = anim.summonFrame !== undefined ? anim.summonFrame : anim.pounceFrame !== undefined ? npc.anims.leap ? anim.pounceFrame : [1,3,4][anim.pounceFrame]! : anim.attackFrame !== undefined ? anim.attackFrame : anim.walkDist > 0 ? walkFrame(anim.walkDist) : idleFrame(anim.idleT);
    const im = this.npcFrame(npc, tint, animName, anim.dir, frame);
    if (!im) return;
    if (anim.heavyWindup !== undefined || anim.pounceWindup !== undefined || anim.meteorWindup !== undefined) {
      // Cached silhouette passes include the club, and remain in the entity
      // painter pass so nearby terrain occludes the tell with the body.
      const strength = 0.65 + 0.35 * (anim.heavyWindup ?? anim.pounceWindup ?? anim.meteorWindup ?? 0);
      const pulse = 0.9 + 0.1 * Math.sin(this._now / 130);
      const x = cx - npc.anchor[0] * u, y = spriteY - npc.anchor[1] * u;
      const w = npc.dims[0] * u, h = npc.dims[1] * u;
      for (const pass of anim.pounceElement === "fire" || anim.meteorWindup !== undefined ? FIRE_SILHOUETTE_PASSES : ICY_SILHOUETTE_PASSES) {
        outlineSprite(ctx, { image: im }, x, y, w, h, pass.dilation * u, pass.color, strength * pulse * pass.alpha);
      }
    }
    ctx.drawImage(im, cx - npc.anchor[0] * u, spriteY - npc.anchor[1] * u, npc.dims[0] * u, npc.dims[1] * u);
  }

  /** Cyan silhouette flash for an enemy (the arena's attack marker) — the
   *  baked 16-sample ring mask, halo + colored passes, mirroring the item
   *  outlines. Drawn before the sprite so the sprite covers the inner half of
   *  the ring, leaving the ~dilation ring around the body. */
  private drawEnemyOutline(
    sx0: number,
    sy0: number,
    u: number,
    npcKind: string,
    tint: string,
    anim: PlayerAnim,
    m: Marker | undefined,
  ): void {
    const style = this.outlineStyle(m);
    if (!style) return;
    const assets = this.heroAssets;
    // family lookup — see drawEnemySprite (kind carries the tint, the atlas
    // is keyed by the sprite family)
    const npc = assets?.atlas.npcs?.[enemyByKind(npcKind)?.family ?? npcKind];
    if (!npc) return;
    const cx = sx0;
    const cy = sy0 + HALF_H * u - (anim.pounceHeight ?? 0) * u; // feet anchor = tile diamond centre
    const animName = anim.summonFrame !== undefined ? npc.anims.summon ? "summon" : "attack" : anim.pounceFrame !== undefined ? npc.anims.leap ? "leap" : "attack" : anim.attackFrame !== undefined ? "attack" : anim.walkDist > 0 ? "walk" : "idle";
    const frame = anim.summonFrame !== undefined ? anim.summonFrame : anim.pounceFrame !== undefined ? npc.anims.leap ? anim.pounceFrame : [1,3,4][anim.pounceFrame]! : anim.attackFrame !== undefined ? anim.attackFrame : anim.walkDist > 0 ? walkFrame(anim.walkDist) : idleFrame(anim.idleT);
    const im = this.npcFrame(npc, tint, animName, anim.dir, frame);
    if (!im) return;
    const cfg = MARKER_CONFIG;
    const dil = style.dilation * u;
    const halo = style.haloWidth * u;
    const destX = cx - npc.anchor[0] * u;
    const destY = cy - npc.anchor[1] * u;
    const destW = npc.dims[0] * u;
    const destH = npc.dims[1] * u;
    outlineSprite(this.ctx, { image: im }, destX, destY, destW, destH, dil + halo, cfg.haloColor, style.alpha * style.haloAlpha);
    outlineSprite(this.ctx, { image: im }, destX, destY, destW, destH, dil, cfg.interactColor, style.alpha);
  }

  /** Cached NPC frame image (tint/anim/dir/frame), keyed by frame path. */
  private npcFrame(npc: NpcDef, tint: string, anim: string, dir: Dir, frame: number): HTMLImageElement | null {
    const version = this.heroAssets?.atlas.version;
    if (version === undefined) return null;
    let paths = npc.frames[tint]?.[anim]?.[dir];
    if (!paths?.length) {
      // A fewer-direction NPC (the 4-dir dragon boss) got a diagonal facing
      // from the 8-way movement math — fold it to a cardinal.
      const dirs = npc.anims[anim]?.dirs;
      const folded = dirs ? foldDir(dir, dirs) : null;
      if (folded && folded !== dir) paths = npc.frames[tint]?.[anim]?.[folded];
    }
    if (!paths?.length) return null;
    const p = paths[Math.min(frame, paths.length - 1)];
    if (!p) return null;
    const frames = this.npcLastFrames.get(npc) ?? new Map<string, HTMLImageElement>();
    this.npcLastFrames.set(npc, frames);
    const key = `${tint}:${dir}`;
    const loadedOrLast = (im: HTMLImageElement): HTMLImageElement | null => {
      if (im.complete && im.naturalWidth > 0) { frames.set(key, im); return im; }
      const last = frames.get(key);
      // Packed-store eviction can invalidate a once-loaded image too.
      return last?.complete && last.naturalWidth > 0 ? last : null;
    };
    const packed = spriteStore();
    if (packed?.has(p)) {
      const im = packed.image(p);
      return loadedOrLast(im);
    }
    let im = this.npcImgCache.get(p);
    if (!im) {
      im = new Image();
      im.src = frameUrl(p, version);
      this.npcImgCache.set(p, im);
    }
    return loadedOrLast(im);
  }

  /** Small name tag above the head, player colour on a dark backing. */
  private nameTag(cx: number, cy: number, u: number, name: string, color: string): void {
    const { ctx } = this;
    const size = Math.max(9, 12 * u);
    ctx.font = `bold ${size}px ui-monospace`;
    ctx.textAlign = "center";
    ctx.textBaseline = "bottom";
    ctx.lineWidth = Math.max(2, 2 * u);
    ctx.strokeStyle = "rgba(6,6,10,0.85)";
    ctx.strokeText(name, cx, cy);
    ctx.fillStyle = color;
    ctx.fillText(name, cx, cy);
  }

  // ---- transient click markers ------------------------------------------

  /** Outline style for the current draw: the click flash when an interact
   *  marker is active on the entity, null otherwise. */
  private outlineStyle(m: Marker | undefined): OutlineStyle | null {
    if (!m) return null;
    return clickOutlineStyle(m, this._now, MARKER_CONFIG);
  }

  /** Ground marker: a flat 2:1 ellipse ripple expanding on the floor, fading
   *  with (1−p)² (R3). Dark halo keeps it readable on light terrain (R7). */
  private drawGroundMarker(sx0: number, sy0: number, u: number, m: Marker & { kind: "ground" }): void {
    const cfg = MARKER_CONFIG;
    const p = markerProgress(m, this._now, cfg);
    const rx = groundRx(p, cfg) * u;
    const ry = rx / cfg.groundEllipseRatio;
    this.ellipseMarker(sx0, sy0 + HALF_H * u, rx, ry, cfg.groundStroke * u, cfg.neutralColor, groundAlpha(p));
  }

  /** Invalid marker: a small fixed gray circle with a decaying horizontal
   *  shake, fading linearly (R5). */
  private drawInvalidMarker(sx0: number, sy0: number, u: number, m: Marker & { kind: "invalid" }): void {
    const cfg = MARKER_CONFIG;
    const p = markerProgress(m, this._now, cfg);
    const shake = invalidShakeX(p, cfg) * u;
    this.ellipseMarker(
      sx0 + shake,
      sy0 + HALF_H * u,
      cfg.invalidRadius * u,
      cfg.invalidRadius * u,
      cfg.invalidStroke * u,
      cfg.invalidColor,
      invalidAlpha(p),
    );
  }

  /** Halo pass then colored stroke (R7) — every marker stroke is drawn twice
   *  so it stays visible on light terrain. */
  private ellipseMarker(cx: number, cy: number, rx: number, ry: number, width: number, color: string, alpha: number): void {
    const cfg = MARKER_CONFIG;
    const { ctx } = this;
    ctx.save();
    ctx.globalAlpha = alpha * cfg.haloAlpha;
    ctx.strokeStyle = cfg.haloColor;
    ctx.lineWidth = width + cfg.haloWidthBonus * this._u;
    ctx.beginPath();
    ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
    ctx.stroke();
    ctx.globalAlpha = alpha;
    ctx.strokeStyle = color;
    ctx.lineWidth = width;
    ctx.beginPath();
    ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
    ctx.stroke();
    ctx.restore();
  }

  /** The tile diamond path in current space (shape outlines). */
  private diamondPath(sx0: number, sy0: number, u: number, c: CanvasRenderingContext2D): void {
    const hw = HALF_W * u;
    const hh = HALF_H * u;
    c.moveTo(sx0, sy0);
    c.lineTo(sx0 + hw, sy0 + hh);
    c.lineTo(sx0, sy0 + TILE_H * u);
    c.lineTo(sx0 - hw, sy0 + hh);
    c.closePath();
  }

  /** Cyan silhouette outline for a procedural shape (door slab, threshold,
   *  boss diamond). Stroked centred on the boundary — the entity's own fill
   *  (drawn afterwards) covers the inner half, leaving a ~dilation ring with
   *  the dark halo ~haloWidth beyond it. */
  private outlineShape(trace: (c: CanvasRenderingContext2D) => void, u: number, style: OutlineStyle): void {
    const cfg = MARKER_CONFIG;
    outlinePath(this.ctx, trace, (style.dilation + style.haloWidth) * u, cfg.haloColor, style.alpha * style.haloAlpha);
    outlinePath(this.ctx, trace, style.dilation * u, cfg.interactColor, style.alpha);
  }

  /** Cyan silhouette outline for a sprite item (key/stone) — the baked
   *  16-sample ring mask, halo + colored passes (R4/R7). */
  private outlineItem(im: HTMLImageElement, cx: number, cy: number, u: number, style: OutlineStyle): void {
    const cfg = MARKER_CONFIG;
    const dil = style.dilation * u;
    const halo = style.haloWidth * u;
    const w = im.naturalWidth * 0.5 * u;
    const h = im.naturalHeight * 0.3125 * u;
    outlineSprite(this.ctx, { image: im }, cx - w / 2, cy - h / 2, w, h, dil + halo, cfg.haloColor, style.alpha * style.haloAlpha);
    outlineSprite(this.ctx, { image: im }, cx - w / 2, cy - h / 2, w, h, dil, cfg.interactColor, style.alpha);
  }

  /** Interact outline for a door: the silhouette of the actual state art
   *  (worldArt — the outline always matches the pixels). */
  private drawDoorOutline(sx0: number, sy0: number, u: number, open: boolean, m: Marker | undefined): void {
    const style = this.outlineStyle(m);
    if (!style) return;
    this.worldArt?.outlineDoor(this.ctx, sx0, sy0, u, open, style);
  }

  /** Interact outline for the boss block / exit portal (same diamond footprint). */
  private drawBossOutline(sx0: number, sy0: number, u: number, m: Marker | undefined): void {
    const style = this.outlineStyle(m);
    if (!style) return;
    this.outlineShape((c) => this.diamondPath(sx0, sy0, u, c), u, style);
  }

  /** The exit portal: the floor's exit once the boss monster is dead.
   *  Placeholder-era procedural canvas (no art-pipeline work) — a bright,
   *  warm oval on the tile's diamond: a gold-white core with a warm rim that
   *  pulses slowly with the world tick. Same tile footprint as the boss
   *  block it replaces (the interact outline is the shared diamond path).
   *  Deliberately never resembles the return portal (dim, cool, near the
   *  base) — bright/warm here, dark/cool there. */
  private drawPortal(sx0: number, sy0: number, u: number, tick: number): void {
    const c = this.ctx;
    // The floor under the glow (the shaft the portal rises out of).
    this.diamond(sx0, sy0, u, "#101014");
    const cx = sx0;
    const cy = sy0 + HALF_H * u * 0.72;
    const rx = HALF_W * u * 0.62;
    const ry = rx * 0.82;
    // Slow pulse on the rim (world tick, 20 tps — one cycle ~2s). The tick is
    // the sim's; no wall clock here.
    const pulse = 0.5 + 0.5 * Math.sin((tick % 40) * (Math.PI / 20));
    // Outer warm rim (the light spilling onto the floor).
    c.save();
    c.globalAlpha = 0.25 + 0.2 * pulse;
    c.fillStyle = "#e8b45a";
    c.beginPath();
    c.ellipse(cx, cy, rx * 1.25, ry * 1.25, 0, 0, Math.PI * 2);
    c.fill();
    c.restore();
    // The rim ring.
    c.strokeStyle = `rgba(240, 196, 110, ${0.65 + 0.3 * pulse})`;
    c.lineWidth = Math.max(1.5, u * 0.14);
    c.beginPath();
    c.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
    c.stroke();
    // The bright core (warm gold-white, brighter toward the centre).
    c.fillStyle = "#f4d992";
    c.beginPath();
    c.ellipse(cx, cy, rx * 0.72, ry * 0.72, 0, 0, Math.PI * 2);
    c.fill();
    c.fillStyle = "#fff6dd";
    c.beginPath();
    c.ellipse(cx, cy, rx * 0.4, ry * 0.4, 0, 0, Math.PI * 2);
    c.fill();
  }

  /** The return portal (run.md): the base room's way back to the hub. The
   *  DELIBERATE mirror of drawPortal — dim and cool ("this one leaves you")
   *  against the exit's bright/warm ("this one finishes the floor"), so the
   *  two never look alike (run.md's look rule, principle #8). A smaller,
   *  darker oval on a slate-blue rim, pulsing off the world tick like the
   *  exit (no wall clock). */
  private drawReturnPortal(sx0: number, sy0: number, u: number, tick: number): void {
    const c = this.ctx;
    this.diamond(sx0, sy0, u, "#0c1116");
    const cx = sx0;
    const cy = sy0 + HALF_H * u * 0.72;
    const rx = HALF_W * u * 0.5;
    const ry = rx * 0.82;
    // A slower, weaker pulse than the exit's — this one is not in a hurry.
    const pulse = 0.5 + 0.5 * Math.sin((tick % 56) * (Math.PI / 28));
    // Outer cool spill (barely there — it is a way out, not a beacon).
    c.save();
    c.globalAlpha = 0.14 + 0.1 * pulse;
    c.fillStyle = "#4f7f9e";
    c.beginPath();
    c.ellipse(cx, cy, rx * 1.2, ry * 1.2, 0, 0, Math.PI * 2);
    c.fill();
    c.restore();
    // The cool rim ring.
    c.strokeStyle = `rgba(120, 168, 196, ${0.45 + 0.25 * pulse})`;
    c.lineWidth = Math.max(1.5, u * 0.12);
    c.beginPath();
    c.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
    c.stroke();
    // The dim core (slate — light you can see, not light that shines).
    c.fillStyle = "#1c3547";
    c.beginPath();
    c.ellipse(cx, cy, rx * 0.72, ry * 0.72, 0, 0, Math.PI * 2);
    c.fill();
    c.fillStyle = "#2e5670";
    c.beginPath();
    c.ellipse(cx, cy, rx * 0.4, ry * 0.4, 0, 0, Math.PI * 2);
    c.fill();
  }

  /** Interact outline for a floor key (the sprite silhouette — missing
   *  atlas draws nothing, like the key itself). */
  private drawKeyOutline(sx0: number, sy0: number, u: number, id: number, m: Marker | undefined): void {
    const style = this.outlineStyle(m);
    if (!style) return;
    const cx = sx0;
    const cy = sy0 + HALF_H * u;
    const im = this.heroAssets ? itemFrameImg(this.heroAssets, "key", "canon") : null;
    if (im) this.outlineItem(im, cx, cy, u, style);
  }

  /** Interact outline for a floor gatestone (the sprite silhouette — missing
   *  atlas draws nothing, like the stone itself). */
  private drawStoneOutline(
    sx0: number,
    sy0: number,
    u: number,
    stoneKind: "personal" | "group",
    ownerId: number,
    m: Marker | undefined,
  ): void {
    const style = this.outlineStyle(m);
    if (!style) return;
    const cx = sx0;
    const cy = sy0 + HALF_H * u;
    const assets = this.heroAssets;
    const im =
      assets && stoneKind === "group"
        ? itemFrameImg(assets, "gatestone-group", "canon")
        : assets
          ? itemFrameImg(assets, "gatestone-personal", playerTint(ownerId))
          : null;
    if (im) this.outlineItem(im, cx, cy, u, style);
  }

  /** Hover outline: diamond around the hovered tile. */
  private drawHover(t: Vec2, u: number): void {
    const sx0 = (t.x - t.y) * HALF_W * u;
    const sy0 = (t.x + t.y) * HALF_H * u;
    this.diamondOutline(sx0, sy0, u, "rgba(255,255,255,0.35)", Math.max(1, 1.5 * u));
  }

  private label(cx: number, cy: number, u: number, text: string, fill: string, sizeFrac: number): void {
    const { ctx } = this;
    ctx.fillStyle = fill;
    ctx.font = `bold ${Math.max(8, TILE_H * u * sizeFrac)}px ui-monospace`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(text, cx, cy + 1);
  }
}
